// src/pages/AdminDashboard.jsx - UPDATED with Review Moderation
// Mobile-first: safe-area, scrollable tabs, card layouts, 48px touch targets
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, X, ExternalLink, BarChart3, RefreshCw, TrendingUp, Users, LogIn, MousePointerClick } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import adminService from '../services/adminService';
const SurveyResultsView = React.lazy(() => import('../components/admin/SurveyResultsView.jsx'));
const TeacherCorrectionsView = React.lazy(() => import('../components/admin/TeacherCorrectionsView.jsx'));
import { getLandingPageStats } from '../utils/analytics';
import reviewModerationService from '../services/reviewModerationService';
import subscriptionService from '../services/subscriptionService';

// Design tokens, align with app (Space Grotesk / Inter, teal accent)
const DEEP_TEAL = '#006D77';
const TEXT_HEADING = '#1E1E1E';
const TEXT_DESC = '#636E72';

// In-memory cache TTL (ms), show cached data immediately, refresh in background when stale
const ADMIN_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Returns the effective score for display, adding writing points when they are stored in
// writingFeedback but were never merged into overallScore (user left before recalculation ran).
// Goethe uses per-module scores (overallScore is null), returned unchanged.
function getEffectiveScore(exam) {
  const raw = exam.overallScore ?? exam.totalScore;
  const max = exam.maxScore;
  if (raw == null || max == null) return { score: raw, max };

  const provider = (exam.provider || '').toUpperCase();
  if (provider === 'GOETHE') return { score: raw, max };

  const writingFeedback = exam.writingFeedback;
  const writingPoints = writingFeedback?.scores?.overallScore || writingFeedback?.overallScore;
  if (!writingPoints) return { score: raw, max };

  // Determine the writing section maximum for this provider/level
  const level = (exam.level || '').toUpperCase();
  const isC1 = level === 'C1';
  let writingMax;
  if (provider === 'OSD' && level !== 'B1') writingMax = 30;
  else if (isC1) writingMax = 48;
  else writingMax = 45;

  // Writing is missing only when the total is just the other sections added up.
  // (This used to assume "missing" for any total up to max - writingMax, which
  // counted the writing twice on most telc results: 156 of 225 showed as 183.)
  const otherSections = Object.entries(exam.sectionScores || {})
    .filter(([id]) => !/^(writing|schreiben)$/i.test(id))
    .map(([, s]) => Number(s?.score));
  if (otherSections.length && otherSections.every(Number.isFinite)) {
    const sum = otherSections.reduce((a, b) => a + b, 0);
    if (Math.abs(raw - sum) > 0.6) return { score: raw, max };
  } else if (raw > max - writingMax) {
    // No section scores to compare with: above the ceiling, writing must be in.
    return { score: raw, max };
  }

  // Writing not yet merged, add it for display only (Firestore data unchanged).
  const score = Math.round((raw + Math.min(writingPoints, writingMax)) * 10) / 10;
  return { score, max };
}

const EXAM_PROVIDER_LABELS = { goethe: 'Goethe', osd: 'ÖSD', telc: 'TELC' };

// Every paper of a given provider+level ships the SAME `examId` (all 12 telc B1 papers
// are "telc-deutsch-b1-mock-001"), so examId alone makes every attempt look identical.
// The attempt document does record which paper was sat, so prefer that: variantName is
// the display label, examFileName the file. Old results predate both fields and fall
// back to the examId.
function describeExamAttempt(exam) {
  const provider = exam.provider || exam.examProvider || null;
  const providerLabel = provider ? (EXAM_PROVIDER_LABELS[String(provider).toLowerCase()] || provider) : null;
  const level = exam.level ? String(exam.level).toUpperCase() : null;
  const examLabel = [providerLabel, level].filter(Boolean).join(' ') || exam.examId || 'Unknown exam';

  // Variant files are named "<examId> - <paper>.json"; strip the shared prefix so only
  // the paper name shows. A file with no " - " is the base paper, i.e. the original.
  const bare = exam.examFileName ? String(exam.examFileName).replace(/\.json$/i, '') : null;
  const fromFile = bare ? (bare.includes(' - ') ? bare.replace(/^.*?\s-\s/, '') : 'Original') : null;
  const paper = exam.variantName || fromFile || null;

  return { examLabel, paper, examId: exam.examId || null };
}

const REFERRAL_SOURCE_LABELS = {
  google: 'Google',
  friends: 'Friends / Word of Mouth',
  youtube: 'YouTube',
  social: 'Social media (Instagram / TikTok)',
  reddit: 'Reddit', // legacy answers from before the option was replaced
  flyers: 'Flyers / Posters'
};

const normalizeReferralSources = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue
      .map((item) => String(item || '').trim().toLowerCase())
      .filter(Boolean);
  }

  if (!rawValue) return [];

  return String(rawValue)
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState('overview');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // In-memory cache so revisiting the dashboard or switching tabs is instant
  const cacheRef = useRef({
    overview: null,   // { users, systemStats, fetchedAt }
    reviews: null,    // { reviews, reviewStats, reviewFilters, fetchedAt }
    consultations: null  // { consultations, fetchedAt }
  });

  // Review moderation state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [selectedReviews, setSelectedReviews] = useState(new Set());
  const [reviewFilters, setReviewFilters] = useState({
    status: 'all',
    sortBy: 'newest'
  });
  const [reviewSearchTerm, setReviewSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Subscription management state
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');

  // All Users sort: default (list order), name A-Z, name Z-A
  const [userSortBy, setUserSortBy] = useState('recent');

  // Consultations (loaded when tab is selected)
  const [consultations, setConsultations] = useState([]);
  const [consultationsLoading, setConsultationsLoading] = useState(false);

  // User history (exams + study sessions) loaded on demand
  const [userHistoryLoaded, setUserHistoryLoaded] = useState(false);
  const [userHistoryLoading, setUserHistoryLoading] = useState(false);

  // Full exam result detail (writing, section scores, user answers), shown in modal
  const [selectedExamDetail, setSelectedExamDetail] = useState(null);

  // GA4 analytics tab
  const [ga4Data, setGa4Data] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(false);
  const [ga4Error, setGa4Error] = useState('');
  const [ga4Days, setGa4Days] = useState(30);
  const [landingStats, setLandingStats] = useState(null);

  const toUserTime = (u) => {
    const login = u.lastLoginAt;
    const activity = u.lastActivity;
    const d1 = login?.toMillis?.() ?? login?.toDate?.()?.getTime?.() ?? (login ? new Date(login).getTime() : 0);
    const d2 = activity?.toMillis?.() ?? activity?.toDate?.()?.getTime?.() ?? (activity ? new Date(activity).getTime() : 0);
    return Math.max(d1, d2);
  };

  const sortedUsers = useMemo(() => {
    if (!users.length) return [];
    if (userSortBy === 'recent') return [...users].sort((a, b) => toUserTime(b) - toUserTime(a));
    const key = (u) => (u.displayName || u.email || '').toLowerCase();
    const sorted = [...users].sort((a, b) => key(a).localeCompare(key(b)));
    return userSortBy === 'name-desc' ? sorted.reverse() : sorted;
  }, [users, userSortBy]);

  const recentUsers = useMemo(() => sortedUsers.slice(0, 5), [sortedUsers]);

  const referralInsights = useMemo(() => {
    const sourceCounts = new Map();
    let usersWithReferral = 0;
    let usersWithOnboarding = 0;

    users.forEach((user) => {
      if (user?.onboardingComplete) usersWithOnboarding += 1;

      const sources = Array.from(new Set(normalizeReferralSources(user?.onboardingData?.heardFrom)));
      if (!sources.length) return;

      usersWithReferral += 1;
      sources.forEach((source) => {
        sourceCounts.set(source, (sourceCounts.get(source) || 0) + 1);
      });
    });

    const sources = Array.from(sourceCounts.entries())
      .map(([source, count]) => ({
        source,
        label: REFERRAL_SOURCE_LABELS[source] || source,
        count,
        pctOfResponders: usersWithReferral > 0 ? Math.round((count / usersWithReferral) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);

    return {
      sources,
      usersWithReferral,
      usersWithOnboarding
    };
  }, [users]);

  const onboardingInsights = useMemo(() => {
    const onboardedUsers = users.filter(u => u.onboardingData);
    const total = onboardedUsers.length;
    if (total === 0) return null;

    const purposeCounts = new Map();
    let withExamDate = 0;
    let takenBeforeCount = 0;
    const providerCounts = new Map();
    const levelCounts = new Map();
    const planCounts = new Map();

    onboardedUsers.forEach((user) => {
      const od = user.onboardingData;

      const purpose = od.purpose || 'unknown';
      purposeCounts.set(purpose, (purposeCounts.get(purpose) || 0) + 1);

      if (od.testDate) withExamDate++;
      if (od.takenBefore === true) takenBeforeCount++;

      const provider = od.examProvider || user.examSelection?.examProvider || user.selectedExamProvider;
      if (provider) providerCounts.set(provider.toUpperCase(), (providerCounts.get(provider.toUpperCase()) || 0) + 1);

      const level = od.level || user.examSelection?.level || user.selectedExamLevel || od.placementLevel;
      if (level) levelCounts.set(level.toUpperCase(), (levelCounts.get(level.toUpperCase()) || 0) + 1);

      const plan = od.pricingPlanId || 'free';
      planCounts.set(plan, (planCounts.get(plan) || 0) + 1);
    });

    const toSorted = (map) =>
      [...map.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);

    const PURPOSE_LABELS = {
      exam_prep: 'Exam Prep',
      general_training: 'General Training',
      both: 'Both',
      unknown: 'Not set',
    };
    const PLAN_LABELS = {
      free: 'Free',
      weekly: '1 Week',
      monthly: '1 Month',
      quarterly: '3 Months',
      premium: 'Premium',
    };

    return {
      total,
      withExamDate,
      withExamDatePct: Math.round((withExamDate / total) * 100),
      takenBefore: takenBeforeCount,
      takenBeforePct: Math.round((takenBeforeCount / total) * 100),
      purposes: toSorted(purposeCounts).map(({ name, count }) => ({
        name: PURPOSE_LABELS[name] || name,
        count,
        pct: Math.round((count / total) * 100),
      })),
      providers: toSorted(providerCounts).map(({ name, count }) => ({
        name,
        count,
        pct: Math.round((count / total) * 100),
      })),
      levels: toSorted(levelCounts).map(({ name, count }) => ({
        name,
        count,
        pct: Math.round((count / total) * 100),
      })),
      plans: toSorted(planCounts).map(({ name, count }) => ({
        name: PLAN_LABELS[name] || name,
        count,
        pct: Math.round((count / total) * 100),
      })),
    };
  }, [users]);

  // Wait for auth readiness before checking admin access to avoid false "Access denied" on first render.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || !adminService.isAdmin()) {
        setError('Access denied. Admin privileges required.');
        return;
      }

      setError('');
      loadOverviewData();
    });

    return () => unsubscribe();
  }, []);

  // Load review data when reviews tab is selected
  useEffect(() => {
    if (currentView === 'reviews') loadReviewData();
  }, [currentView, reviewFilters]);

  // Load consultations when Consultations tab is selected
  useEffect(() => {
    if (currentView === 'consultations') loadConsultations();
  }, [currentView]);

  // Load GA4 data when analytics tab is selected or days changes
  useEffect(() => {
    if (currentView === 'analytics') loadGA4Data(ga4Days);
  }, [currentView, ga4Days]);

  const loadOverviewData = async () => {
    const cache = cacheRef.current.overview;
    const useCache = cache && (Date.now() - cache.fetchedAt) < ADMIN_CACHE_TTL_MS;

    if (useCache) {
      setUsers(cache.users ?? []);
      setSystemStats(cache.systemStats ?? null);
      setLoading(false);
      // Refresh in background; update cache and state when done
      try {
        const [usersResult, statsResult] = await Promise.all([
          adminService.getAllUsers(),
          adminService.getSystemStats()
        ]);
        const nextUsers = usersResult.success ? usersResult.users : cache.users;
        const nextStats = statsResult.success ? statsResult.stats : cache.systemStats;
        if (usersResult.success) setUsers(nextUsers);
        if (statsResult.success) setSystemStats(nextStats);
        cacheRef.current.overview = { users: nextUsers ?? [], systemStats: nextStats ?? null, fetchedAt: Date.now() };
      } catch (_) { /* keep showing cached data */ }
      return;
    }

    setLoading(true);
    try {
      const [usersResult, statsResult] = await Promise.all([
        adminService.getAllUsers(),
        adminService.getSystemStats()
      ]);

      const nextUsers = usersResult.success ? usersResult.users : [];
      const nextStats = statsResult.success ? statsResult.stats : null;
      if (usersResult.success) setUsers(nextUsers);
      if (statsResult.success) setSystemStats(nextStats);
      cacheRef.current.overview = { users: nextUsers, systemStats: nextStats, fetchedAt: Date.now() };
    } catch (err) {
      setError('Error loading data: ' + err.message);
    }
    setLoading(false);
  };

  const loadReviewData = async () => {
    const cache = cacheRef.current.reviews;
    const filtersMatch = cache && JSON.stringify(cache.reviewFilters) === JSON.stringify(reviewFilters);
    const useCache = filtersMatch && cache && (Date.now() - cache.fetchedAt) < ADMIN_CACHE_TTL_MS;

    if (useCache) {
      setReviews(cache.reviews ?? []);
      setReviewStats(cache.reviewStats ?? null);
      setLoading(false);
      try {
        const [reviewsResult, statsResult] = await Promise.all([
          reviewModerationService.getAllReviewsForModeration(reviewFilters),
          reviewModerationService.getModerationStats()
        ]);
        setReviews(reviewsResult);
        setReviewStats(statsResult);
        cacheRef.current.reviews = { reviews: reviewsResult, reviewStats: statsResult, reviewFilters: { ...reviewFilters }, fetchedAt: Date.now() };
      } catch (_) { /* keep cached */ }
      return;
    }

    setLoading(true);
    try {
      const [reviewsResult, statsResult] = await Promise.all([
        reviewModerationService.getAllReviewsForModeration(reviewFilters),
        reviewModerationService.getModerationStats()
      ]);

      setReviews(reviewsResult);
      setReviewStats(statsResult);
      cacheRef.current.reviews = { reviews: reviewsResult, reviewStats: statsResult, reviewFilters: { ...reviewFilters }, fetchedAt: Date.now() };
    } catch (err) {
      console.error('Error loading review data:', err);
      setError('Error loading review data: ' + err.message);
    }
    setLoading(false);
  };

  const loadUserDetails = async (userId) => {
    setLoading(true);
    setSubscriptionError('');
    setUserHistoryLoaded(false);
    try {
      const result = await adminService.getUserDetails(userId);
      if (result.success) {
        setUserDetails(result.userDetails);
        setSelectedUser(userId);
        setCurrentView('user-details');
        await loadUserSubscriptionStatus(userId);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error loading user details: ' + err.message);
    }
    setLoading(false);
  };

  const loadUserHistory = async () => {
    if (!selectedUser) return;
    setUserHistoryLoading(true);
    try {
      const result = await adminService.getUserHistory(selectedUser);
      if (result.success && userDetails) {
        setUserDetails(prev => ({
          ...prev,
          examResults: result.examResults || [],
          studySessions: result.studySessions || [],
          stats: {
            ...prev.stats,
            totalExams: (result.examResults || []).length,
            totalStudySessions: (result.studySessions || []).length,
            averageExamScore: result.averageExamScore ?? prev.stats.averageExamScore
          }
        }));
        setUserHistoryLoaded(true);
      }
    } catch (err) {
      console.error('Error loading user history:', err);
    }
    setUserHistoryLoading(false);
  };

  const loadConsultations = async () => {
    const cache = cacheRef.current.consultations;
    const useCache = cache && (Date.now() - cache.fetchedAt) < ADMIN_CACHE_TTL_MS;

    if (useCache) {
      setConsultations(cache.consultations ?? []);
      setConsultationsLoading(false);
      try {
        const result = await adminService.getConsultationsForAdmin();
        if (result.success) {
          setConsultations(result.consultations || []);
          cacheRef.current.consultations = { consultations: result.consultations || [], fetchedAt: Date.now() };
        }
      } catch (_) { /* keep cached */ }
      return;
    }

    setConsultationsLoading(true);
    try {
      const result = await adminService.getConsultationsForAdmin();
      if (result.success) {
        setConsultations(result.consultations || []);
        cacheRef.current.consultations = { consultations: result.consultations || [], fetchedAt: Date.now() };
      }
    } catch (err) {
      console.error('Error loading consultations:', err);
    }
    setConsultationsLoading(false);
  };

  const loadGA4Data = async (days) => {
    setGa4Loading(true);
    setGa4Error('');
    try {
      const fn = httpsCallable(functions, 'getGA4Analytics');
      const result = await fn({ days });
      setGa4Data(result.data);
    } catch (err) {
      console.error('GA4 fetch error:', err);
      setGa4Error(err.message || 'Failed to load analytics data');
    } finally {
      setGa4Loading(false);
    }
    // Load landing page engagement stats (scroll depth + time on page) from Firestore
    getLandingPageStats().then((data) => { if (data) setLandingStats(data); });
  };

  const loadUserSubscriptionStatus = async (userId) => {
    try {
      setSubscriptionLoading(true);
      const status = await subscriptionService.getSubscriptionStatus(userId, {
        skipAdminOverride: true,
        disableSyncWrite: true,
      });
      setUserSubscriptionStatus(status);
    } catch (err) {
      console.error('Error loading subscription status:', err);
      setUserSubscriptionStatus(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleAssignSubscription = async (planType) => {
    if (!selectedUser) return;
    
    const planName = planType === 'weekly' ? '1 week' : planType === 'monthly' ? '1 month' : planType === 'premium' ? 'Premium (6 months + 5 sessions + 10 corrections)' : '3 months';
    if (!window.confirm(`Assign ${planName} subscription to this user?`)) {
      return;
    }

    setSubscriptionLoading(true);
    setSubscriptionError('');
    
    try {
      const result = await subscriptionService.assignSubscription(selectedUser, planType);
      if (result.success) {
        alert(`Successfully assigned ${planName} subscription!`);
        // Reload user details and subscription status
        await loadUserDetails(selectedUser);
      } else {
        setSubscriptionError(result.error || 'Failed to assign subscription');
        alert('Error: ' + (result.error || 'Failed to assign subscription'));
      }
    } catch (err) {
      const errorMsg = 'Error assigning subscription: ' + err.message;
      setSubscriptionError(errorMsg);
      alert(errorMsg);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleRevokeSubscription = async () => {
    if (!selectedUser) return;
    
    if (!window.confirm('Are you sure you want to revoke this user\'s subscription?')) {
      return;
    }

    setSubscriptionLoading(true);
    setSubscriptionError('');
    
    try {
      const result = await subscriptionService.revokeSubscription(selectedUser);
      if (result.success) {
        alert(result.message || t('message.success'));
        // Reload user details and subscription status
        await loadUserDetails(selectedUser);
      } else {
        setSubscriptionError(result.error || 'Failed to revoke subscription');
        alert('Error: ' + (result.error || 'Failed to revoke subscription'));
      }
    } catch (err) {
      const errorMsg = 'Error revoking subscription: ' + err.message;
      setSubscriptionError(errorMsg);
      alert(errorMsg);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleDeleteExam = async (userId, examId) => {
    if (window.confirm('Are you sure you want to delete this exam result?')) {
      const result = await adminService.deleteUserExam(userId, examId);
      if (result.success) {
        alert('Exam deleted successfully');
        loadUserDetails(userId); // Reload user details
      } else {
        alert('Error deleting exam: ' + result.error);
      }
    }
  };

  // Review moderation functions
  const handleSingleReviewAction = async (reviewId, action, note = '') => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'publish':
          await reviewModerationService.updateReviewStatus(reviewId, 'published', note);
          break;
        case 'hide':
          await reviewModerationService.updateReviewStatus(reviewId, 'hidden', note);
          break;
        case 'delete':
          if (window.confirm('Sind Sie sicher, dass Sie diese Bewertung permanent löschen möchten?')) {
            await reviewModerationService.deleteReview(reviewId, note);
          } else {
            return;
          }
          break;
        case 'clear_reports':
          await reviewModerationService.clearReports(reviewId);
          break;
      }

      await loadReviewData();
    } catch (error) {
      console.error('Error performing action:', error);
      alert('Fehler beim Ausführen der Aktion: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkReviewAction = async (action) => {
    if (selectedReviews.size === 0) {
      alert('Bitte wählen Sie mindestens eine Bewertung aus.');
      return;
    }

    const note = prompt(`Notiz für ${action} (optional):`);
    if (note === null) return; // User cancelled

    setActionLoading(true);
    try {
      const results = await reviewModerationService.bulkAction(Array.from(selectedReviews), action, note);

      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      alert(`Aktion abgeschlossen: ${successful} erfolgreich, ${failed} fehlgeschlagen`);

      setSelectedReviews(new Set());
      await loadReviewData();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Fehler bei der Massenbearbeitung: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleReviewSelection = (reviewId) => {
    const newSelection = new Set(selectedReviews);
    if (newSelection.has(reviewId)) {
      newSelection.delete(reviewId);
    } else {
      newSelection.add(reviewId);
    }
    setSelectedReviews(newSelection);
  };

  const getReviewStatusBadge = (review) => {
    const { status, reportCount } = review;

    if (reportCount > 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          🚩 {reportCount} Meldung{reportCount > 1 ? 'en' : ''}
        </span>
      );
    }

    switch (status) {
      case 'published':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            ✅ Veröffentlicht
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            ⏳ Ausstehend
          </span>
        );
      case 'hidden':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            👁️‍🗨️ Versteckt
          </span>
        );
      default:
        return null;
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (!reviewSearchTerm) return true;
    const searchLower = reviewSearchTerm.toLowerCase();
    return (
      review.comment.toLowerCase().includes(searchLower) ||
      review.userName.toLowerCase().includes(searchLower) ||
      review.userEmail.toLowerCase().includes(searchLower) ||
      (review.title && review.title.toLowerCase().includes(searchLower))
    );
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const isUserSubscribed = (user) => {
    const status = user?.subscriptionStatus || 'free';
    if (status !== 'active') return false;
    const endDate = user?.subscriptionEndDate?.toMillis?.() != null
      ? user.subscriptionEndDate.toDate()
      : user?.subscriptionEndDate;
    if (!endDate) return true;
    return new Date(endDate) > new Date();
  };

  const getUserExamInfo = (user) => {
    const provider = user?.examSelection?.examProvider ?? user?.selectedExamProvider ?? null;
    const level =
      user?.examSelection?.level ??
      user?.selectedExamLevel ??
      user?.onboardingData?.placementLevel ??
      null;
    const providerLabel = provider ? (EXAM_PROVIDER_LABELS[provider] || provider) : null;
    return { provider: providerLabel, level: level || null };
  };

  const getLevelDisplay = (user, examInfo) => {
    if (examInfo?.level) return examInfo.level;
    return user?.onboardingComplete ? 'Set in onboarding' : 'Onboarding pending';
  };

  const navRef = useRef(null);

  const adminTabs = [
    { id: 'overview', label: 'Overview', labelShort: 'Overview', onClick: () => { setCurrentView('overview'); loadOverviewData(); } },
    { id: 'users', label: 'All Users', labelShort: 'Users', onClick: () => setCurrentView('users') },
    { id: 'reviews', label: reviewStats != null ? `Reviews (${reviewStats.totalReviews})` : 'Reviews', labelShort: reviewStats != null ? `Reviews (${reviewStats.totalReviews})` : 'Reviews', onClick: () => setCurrentView('reviews') },
    { id: 'consultations', label: 'Consultations', labelShort: 'Consultations', onClick: () => setCurrentView('consultations') },
    { id: 'analytics', label: 'User journey', labelShort: 'Journey', onClick: () => setCurrentView('analytics') },
    { id: 'onboarding', label: 'Onboarding', labelShort: 'Onboarding', onClick: () => { setCurrentView('onboarding'); if (!users.length) loadOverviewData(); } },
    { id: 'survey', label: 'Survey', labelShort: 'Survey', onClick: () => setCurrentView('survey') },
    { id: 'corrections', label: 'Corrections', labelShort: 'Corrections', onClick: () => setCurrentView('corrections') },
  ];

  // Mobile: show 3 tabs at a time; arrow shifts which 3 are visible
  const MOBILE_VISIBLE_TABS = 3;
  const [mobileTabOffset, setMobileTabOffset] = useState(0);
  const maxMobileOffset = Math.max(0, adminTabs.length - MOBILE_VISIBLE_TABS);
  const visibleTabs = adminTabs.slice(mobileTabOffset, mobileTabOffset + MOBILE_VISIBLE_TABS);

  // Keep visible window in sync with active tab (e.g. switch to Consultations slides to show it)
  useEffect(() => {
    const idx = adminTabs.findIndex((t) => t.id === currentView);
    if (idx < 0) return;
    const maxO = Math.max(0, adminTabs.length - MOBILE_VISIBLE_TABS);
    const want = Math.max(0, Math.min(idx, maxO));
    setMobileTabOffset((prev) => (prev !== want ? want : prev));
  }, [currentView]);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 safe-area-inset"
        style={{ paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <div className="admin-error rounded-2xl p-5 text-center max-w-md text-red-600 font-medium bg-red-50 border border-red-200">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      className="admin-dashboard min-h-full bg-[#F8FAFA] safe-area-inset"
      style={{
        paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        paddingLeft: 'max(1rem, env(safe-area-inset-left))',
        paddingRight: 'max(1rem, env(safe-area-inset-right))',
      }}
    >
      <div className="admin-header-bleed">
        <header className="admin-header">
          <h1 className="admin-title">Admin Dashboard</h1>
          <nav ref={navRef} className="admin-nav" role="tablist" aria-label="Admin sections">
            {/* Single row: mobile = arrows + 3 tabs, desktop = all tabs (no arrows) */}
            <div className="admin-nav-mobile flex md:hidden items-center gap-1">
              {mobileTabOffset > 0 && (
                <button
                  type="button"
                  onClick={() => setMobileTabOffset((o) => Math.max(0, o - 1))}
                  className="admin-nav-arrow min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg border-0 bg-white/20 text-white touch-manipulation"
                  aria-label="Previous tabs"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="flex flex-1 justify-center gap-1 min-w-0">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={currentView === tab.id}
                    className={`admin-nav-btn flex-1 min-w-0 ${currentView === tab.id ? 'active' : ''}`}
                    onClick={tab.onClick}
                  >
                    <span className="truncate block">{tab.labelShort}</span>
                  </button>
                ))}
              </div>
              {mobileTabOffset < maxMobileOffset && (
                <button
                  type="button"
                  onClick={() => setMobileTabOffset((o) => Math.min(maxMobileOffset, o + 1))}
                  className="admin-nav-arrow min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg border-0 bg-white/20 text-white touch-manipulation"
                  aria-label="Next tabs"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
            {/* Desktop: all tabs, use only Tailwind so hidden isn't overridden by .admin-nav-inner { display: flex } */}
            <div className="hidden md:flex md:items-center md:gap-2 md:pb-3 md:px-1">
              {adminTabs.map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={currentView === tab.id}
                  className={`admin-nav-btn ${currentView === tab.id ? 'active' : ''}`}
                  onClick={tab.onClick}
                >
                  <span className="sm:hidden">{tab.labelShort}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </header>
      </div>

      {loading && <div className="admin-loading">Loading…</div>}

      <main className="admin-content overflow-x-hidden">
      {/* Overview Section */}
      {currentView === 'overview' && systemStats && (
        <div className="admin-overview">
          <h2 className="admin-section-title">System Statistics</h2>
          <div className="relative bg-white rounded-2xl border-2 border-black/10 overflow-hidden">
            <div className="relative flex items-stretch">
              <div className="flex-1 flex flex-col items-center justify-center py-3.5 sm:py-4 px-1 gap-1">
                <p className="font-space text-lg sm:text-xl font-black text-black leading-none">{systemStats.totalUsers}</p>
                <p className="text-[9px] sm:text-[10px] text-black/35 font-semibold uppercase tracking-wider">Total Users</p>
              </div>
              <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />
              <div className="flex-1 flex flex-col items-center justify-center py-3.5 sm:py-4 px-1 gap-1">
                <p className="font-space text-lg sm:text-xl font-black text-black leading-none">{systemStats.premiumUsers}</p>
                <p className="text-[9px] sm:text-[10px] text-black/35 font-semibold uppercase tracking-wider">Premium Users</p>
                {systemStats.premiumRate != null && (
                  <p className="text-[10px] text-black/45">{systemStats.premiumRate}% of users</p>
                )}
              </div>
              <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />
              <div className="flex-1 flex flex-col items-center justify-center py-3.5 sm:py-4 px-1 gap-1">
                <p className="font-space text-lg sm:text-xl font-black text-black leading-none">{systemStats.activeUsers}</p>
                <p className="text-[9px] sm:text-[10px] text-black/35 font-semibold uppercase tracking-wider">Active Users</p>
                {systemStats.activityRate != null && (
                  <p className="text-[10px] text-black/45">{systemStats.activityRate}% activity</p>
                )}
              </div>
            </div>
          </div>

          <h3 className="admin-subtitle">Recent Users</h3>
          <p className="text-sm mb-2" style={{ color: TEXT_DESC }}>By last login or activity</p>
          <div className="recent-users">
            {recentUsers.map(user => (
              <div key={user.id} className="user-card" onClick={() => loadUserDetails(user.id)}>
                <h4>{user.displayName || user.email}</h4>
                <p>Last Login: {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}</p>
                <p>Last Seen: {formatDate(user.lastActivity)}</p>
                <p>Exams: {user.totalExams}</p>
                <span className={`status ${user.isActive ? 'active' : 'inactive'}`}>
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>

          <h3 className="admin-subtitle mt-6">Onboarding Referral Sources</h3>
          <p className="text-sm mb-2" style={{ color: TEXT_DESC }}>
            {referralInsights.usersWithReferral > 0
              ? `${referralInsights.usersWithReferral} users submitted where they heard about Logami.`
              : 'No referral source data yet.'}
          </p>
          <div className="admin-card rounded-2xl border border-[#E1E4E8] bg-white shadow-sm p-4">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="inline-flex items-center rounded-full bg-[#E6F7F8] text-[#006D77] px-3 py-1 text-xs font-semibold">
                Responses: {referralInsights.usersWithReferral}
              </span>
              <span className="inline-flex items-center rounded-full bg-black/5 text-black/70 px-3 py-1 text-xs font-semibold">
                Onboarded users: {referralInsights.usersWithOnboarding}
              </span>
            </div>

            {referralInsights.sources.length === 0 ? (
              <p className="text-sm text-black/55">No onboarding referral responses recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {referralInsights.sources.map((item) => (
                  <div key={item.source}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-black/80">{item.label}</span>
                      <span className="text-xs font-bold text-black/55">{item.count} ({item.pctOfResponders}%)</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                      <div
                        className="h-full rounded-full bg-[#006D77]"
                        style={{ width: `${item.pctOfResponders}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reviews Section - NEW */}
      {currentView === 'reviews' && (
        <div className="admin-reviews">
          <div className="admin-reviews-header">
            <h2 className="admin-section-title">Review Moderation</h2>
            <button
              onClick={loadReviewData}
              disabled={actionLoading}
              className="admin-btn admin-btn-primary min-h-[48px] touch-manipulation"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {/* Review Stats, same 2-col grid as System Statistics for mobile */}
          {reviewStats && (
            <div className="admin-reviews-stats stats-grid">
              <div className="stat-card">
                <h3>Total Reviews</h3>
                <p className="stat-number">{reviewStats.totalReviews}</p>
              </div>
              <div className="stat-card">
                <h3>Published</h3>
                <p className="stat-number">{reviewStats.publishedReviews}</p>
              </div>
              <div className="stat-card">
                <h3>Reported</h3>
                <p className="stat-number">{reviewStats.reportedReviews}</p>
              </div>
              <div className="stat-card">
                <h3>Avg Rating</h3>
                <p className="stat-number">{reviewStats.averageRating}</p>
              </div>
            </div>
          )}

          {/* Review Controls, search full width, filters in one row on mobile */}
          <div className="admin-reviews-controls admin-card rounded-2xl border border-[#E1E4E8] bg-white shadow-sm p-4">
            <div className="flex flex-col gap-3">
              <input
                type="text"
                placeholder="Search reviews…"
                value={reviewSearchTerm}
                onChange={(e) => setReviewSearchTerm(e.target.value)}
                className="w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 focus:border-[#006D77] touch-manipulation"
                aria-label="Search reviews"
              />
              <div className="flex gap-2 flex-wrap">
                <select
                  value={reviewFilters.status}
                  onChange={(e) => setReviewFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="admin-review-select flex-1 min-w-[120px] px-3 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 touch-manipulation"
                  aria-label="Filter by status"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="pending">Pending</option>
                  <option value="hidden">Hidden</option>
                  <option value="reported">Reported</option>
                </select>
                <select
                  value={reviewFilters.sortBy}
                  onChange={(e) => setReviewFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  className="admin-review-select flex-1 min-w-[120px] px-3 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 touch-manipulation"
                  aria-label="Sort reviews"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="most_reported">Most Reported</option>
                  <option value="rating_high">Highest Rating</option>
                  <option value="rating_low">Lowest Rating</option>
                </select>
              </div>
            </div>

            {/* Bulk Actions, touch-friendly buttons */}
            {selectedReviews.size > 0 && (
              <div className="admin-reviews-bulk mt-4 p-3 rounded-xl bg-[#E6F7F8] border border-[#83C5BE]/40">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <span className="text-sm font-medium text-[#006D77]">
                    {selectedReviews.size} review{selectedReviews.size > 1 ? 's' : ''} selected
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleBulkReviewAction('publish')}
                      disabled={actionLoading}
                      className="admin-review-action admin-review-action--publish min-h-[48px] px-4 rounded-xl font-semibold text-sm touch-manipulation"
                    >
                      Publish
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkReviewAction('hide')}
                      disabled={actionLoading}
                      className="admin-review-action admin-review-action--hide min-h-[48px] px-4 rounded-xl font-semibold text-sm touch-manipulation"
                    >
                      Hide
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkReviewAction('delete')}
                      disabled={actionLoading}
                      className="admin-review-action admin-review-action--delete min-h-[48px] px-4 rounded-xl font-semibold text-sm touch-manipulation"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Reviews List, mobile-optimized cards */}
          <div className="admin-reviews-list space-y-3 sm:space-y-4">
            {filteredReviews.length === 0 ? (
              <div className="admin-reviews-empty bg-white rounded-2xl border border-[#E1E4E8] p-6 sm:p-12 text-center">
                <h3 className="admin-subtitle mb-2">No reviews found</h3>
                <p className="text-sm" style={{ color: TEXT_DESC }}>
                  {reviewSearchTerm ? 'No reviews match your search.' : 'No reviews available.'}
                </p>
              </div>
            ) : (
              filteredReviews.map(review => (
                <div key={review.id} className="admin-review-card admin-card bg-white rounded-2xl border border-[#E1E4E8] shadow-sm overflow-hidden">
                  {/* Row 1: checkbox, name, stars, status, wraps on small screens */}
                  <div className="p-4 sm:p-5">
                    <div className="admin-review-card__row1 flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <input
                        type="checkbox"
                        checked={selectedReviews.has(review.id)}
                        onChange={() => toggleReviewSelection(review.id)}
                        className="admin-review-checkbox rounded border-[#E1E4E8] w-5 h-5 flex-shrink-0 touch-manipulation"
                        aria-label={`Select review by ${review.userName}`}
                      />
                      <span className="font-semibold text-base truncate max-w-[40%] sm:max-w-none" style={{ color: TEXT_HEADING }}>
                        {review.userName}
                      </span>
                      {review.verified && <span className="text-[#006D77]" aria-hidden>🛡️</span>}
                      <div className="flex items-center gap-0.5" aria-label={`Rating ${review.rating} of 5`}>
                        {Array.from({ length: 5 }, (_, i) => (
                          <span key={i} className={i < review.rating ? 'text-amber-400' : 'text-gray-300'}>⭐</span>
                        ))}
                      </div>
                      {getReviewStatusBadge(review)}
                    </div>

                    <div className="text-xs sm:text-sm mb-3 truncate" style={{ color: TEXT_DESC }}>
                      {review.userEmail} · {reviewModerationService.formatRelativeTime(review.createdAt)}
                      {review.plan && <span> · {review.plan}</span>}
                    </div>

                    {review.title && (
                      <h4 className="font-semibold text-sm mb-1" style={{ color: TEXT_HEADING }}>{review.title}</h4>
                    )}
                    <p className="text-sm sm:text-base mb-3 line-clamp-2 sm:line-clamp-none" style={{ color: TEXT_DESC }}>"{review.comment}"</p>

                    {(review.moderatedAt || review.lastReportedAt) && (
                      <div className="bg-[#F8FAFA] rounded-xl p-2.5 sm:p-3 text-xs sm:text-sm mb-3 border border-[#E1E4E8]">
                        {review.moderatedAt && (
                          <p className="mb-1" style={{ color: TEXT_DESC }}>
                            <strong>Moderated:</strong> {reviewModerationService.formatRelativeTime(review.moderatedAt)}
                            {review.moderationNote && <span>, {review.moderationNote}</span>}
                          </p>
                        )}
                        {review.lastReportedAt && (
                          <p className="text-red-600">
                            <strong>Reported:</strong> {reviewModerationService.formatRelativeTime(review.lastReportedAt)}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Actions, full-width row, 48px touch targets */}
                    <div className="admin-review-card__actions flex flex-wrap gap-2 pt-3 border-t border-[#E1E4E8]">
                      {review.status === 'hidden' && (
                        <button
                          type="button"
                          onClick={() => handleSingleReviewAction(review.id, 'publish')}
                          disabled={actionLoading}
                          className="admin-review-action admin-review-action--publish min-h-[48px] px-3 rounded-xl text-sm font-semibold touch-manipulation"
                        >
                          Publish
                        </button>
                      )}
                      {review.status === 'published' && (
                        <button
                          type="button"
                          onClick={() => handleSingleReviewAction(review.id, 'hide')}
                          disabled={actionLoading}
                          className="admin-review-action admin-review-action--hide min-h-[48px] px-3 rounded-xl text-sm font-semibold touch-manipulation"
                        >
                          Hide
                        </button>
                      )}
                      {review.reportCount > 0 && (
                        <button
                          type="button"
                          onClick={() => handleSingleReviewAction(review.id, 'clear_reports')}
                          disabled={actionLoading}
                          className="admin-review-action admin-review-action--clear min-h-[48px] px-3 rounded-xl text-sm font-semibold touch-manipulation"
                        >
                          Clear reports
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const reason = prompt('Reason for deletion:');
                          if (reason !== null) handleSingleReviewAction(review.id, 'delete', reason);
                        }}
                        disabled={actionLoading}
                        className="admin-review-action admin-review-action--delete min-h-[48px] px-3 rounded-xl text-sm font-semibold touch-manipulation"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="px-4 sm:px-5 pb-3 flex items-center justify-between text-xs" style={{ color: TEXT_DESC }}>
                    <span>Helpful: {review.helpful || 0}{review.reportCount > 0 && ` · 🚩 ${review.reportCount} report${review.reportCount > 1 ? 's' : ''}`}</span>
                    <span>ID: {review.id.slice(-8)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* All Users Section */}
      {currentView === 'users' && (
        <div className="admin-users">
          <div className="admin-reviews-header admin-reviews-header--wrap">
            <h2 className="admin-section-title">All Users ({users.length})</h2>
            <label className="flex items-center gap-2 min-h-[48px]">
              <span className="text-sm font-medium whitespace-nowrap" style={{ color: TEXT_DESC }}>Sort:</span>
              <select
                value={userSortBy}
                onChange={(e) => setUserSortBy(e.target.value)}
                className="admin-sort-select min-h-[48px] px-3 py-2 text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 touch-manipulation"
                aria-label="Sort users"
              >
                <option value="recent">By recent (last login/activity)</option>
                <option value="name-asc">A → Z (name)</option>
                <option value="name-desc">Z → A (name)</option>
              </select>
            </label>
          </div>
          {/* Mobile: card list. Desktop: table */}
          <div className="admin-users-cards md:hidden space-y-3">
            {sortedUsers.map((user) => {
              const examInfo = getUserExamInfo(user);
              return (
              <div
                key={user.id}
                role="button"
                tabIndex={0}
                className="admin-user-card rounded-2xl bg-white border border-[#E1E4E8] p-4 shadow-sm active:scale-[0.99] touch-manipulation cursor-pointer hover:bg-[#006D77]/5 hover:border-[#006D77]/30 transition-colors"
                onClick={() => loadUserDetails(user.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadUserDetails(user.id); } }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <p className="font-semibold text-base truncate" style={{ color: TEXT_HEADING }}>
                        {user.displayName || user.email}
                      </p>
                      {(examInfo.provider || examInfo.level) && (
                        <span className="text-xs font-medium text-black/50 shrink-0">
                          {[examInfo.provider, examInfo.level].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm truncate mt-0.5" style={{ color: TEXT_DESC }}>
                      {user.email}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`status ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className={isUserSubscribed(user) ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                        {isUserSubscribed(user) ? '✓ Subscribed' : ', '}
                      </span>
                    </div>
                    <p className="text-xs mt-2" style={{ color: TEXT_DESC }}>
                      Last seen: {user.lastActivity ? formatDate(user.lastActivity) : 'Never'} · Exams: {user.totalExams}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); loadUserDetails(user.id); }}
                    className="admin-btn admin-btn-primary shrink-0 min-h-[48px] min-w-[48px] touch-manipulation"
                    style={{ background: DEEP_TEAL, color: '#fff' }}
                    aria-label="View user details"
                  >
                    View
                  </button>
                </div>
              </div>
              );
            })}
          </div>
          <div className="users-table hidden md:block">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Exam provider</th>
                  <th>Level</th>
                  <th>Subscribed</th>
                  <th>Last Login</th>
                  <th>Last Seen</th>
                  <th>Exams</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(user => {
                  const examInfo = getUserExamInfo(user);
                  return (
                  <tr
                    key={user.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => loadUserDetails(user.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadUserDetails(user.id); } }}
                    className="cursor-pointer hover:bg-[#006D77]/5 transition-colors"
                  >
                    <td>{user.email}</td>
                    <td>{user.displayName || '-'}</td>
                    <td>{examInfo.provider || ', '}</td>
                    <td>{getLevelDisplay(user, examInfo)}</td>
                    <td>
                      <span className={isUserSubscribed(user) ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                        {isUserSubscribed(user) ? '✓' : ', '}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div className="text-sm font-medium">
                          {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                        </div>
                        {user.lastLoginDevice && (
                          <div className="text-xs text-gray-500">
                            {user.lastLoginDevice}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{user.lastActivity ? formatDate(user.lastActivity) : 'Never'}</td>
                    <td>{user.totalExams}</td>
                    <td>
                      <span className={`status ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => loadUserDetails(user.id)}
                        className="admin-btn min-h-[44px] touch-manipulation"
                        style={{ background: DEEP_TEAL, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: '0.875rem' }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Details Section */}
      {currentView === 'user-details' && userDetails && (
        <div className="admin-user-details">
          <button
            type="button"
            onClick={() => setCurrentView('users')}
            className="admin-back-button min-h-[48px] touch-manipulation"
          >
            ← Back to Users
          </button>

          <h2 className="admin-section-title mt-4">User Details</h2>
          <p className="text-sm mt-1 break-all" style={{ color: TEXT_DESC }}>{userDetails.profile.email}</p>

          <div className="user-info-grid">
            <div className="info-section">
              <h3 className="admin-subtitle">Profile Information</h3>
              <p><strong>Email:</strong> {userDetails.profile.email}</p>
              <p><strong>Display Name:</strong> {userDetails.profile.displayName || 'Not set'}</p>
              <p><strong>Subscribed:</strong> {isUserSubscribed(userDetails.profile) ? 'Yes' : 'No'}</p>
              <p>
                <strong>Heard About Us:</strong>{' '}
                {(() => {
                  const sources = normalizeReferralSources(userDetails.profile?.onboardingData?.heardFrom);
                  if (!sources.length) return 'Not provided';
                  return sources.map((source) => REFERRAL_SOURCE_LABELS[source] || source).join(', ');
                })()}
              </p>
              <p><strong>Created:</strong> {formatDate(userDetails.profile.createdAt)}</p>
              <p><strong>Last Updated:</strong> {formatDate(userDetails.profile.updatedAt)}</p>
              <p><strong>Last Login:</strong> {userDetails.profile.lastLoginAt ? formatDate(userDetails.profile.lastLoginAt) : 'Never'}</p>
              {userDetails.profile.lastLoginDevice && (
                <p><strong>Login Device:</strong> {userDetails.profile.lastLoginDevice}</p>
              )}
            </div>

            <div className="info-section">
              <h3 className="admin-subtitle">Statistics</h3>
              <p><strong>Study Sessions:</strong> {userHistoryLoaded ? userDetails.stats.totalStudySessions : ', '}</p>
              <p><strong>Exams Completed:</strong> {userHistoryLoaded ? userDetails.stats.totalExams : ', '}</p>
              <p><strong>Average Exam Score:</strong> {userHistoryLoaded ? `${userDetails.stats.averageExamScore}%` : ', '}</p>
              <p><strong>Study Streak:</strong> {userDetails.stats.studyStreak ?? 0} days</p>
            </div>

            <div className="info-section">
              <h3 className="admin-subtitle">Subscription Management</h3>
              {subscriptionLoading ? (
                <p>Loading subscription status...</p>
              ) : userSubscriptionStatus ? (
                <div>
                  <p><strong>Status:</strong> 
                    <span className={userSubscriptionStatus.isActive ? 'text-green-600 font-semibold' : 'text-gray-600'}>
                      {userSubscriptionStatus.isActive ? ' ✅ Active' : ' ❌ Inactive'}
                    </span>
                  </p>
                  <p><strong>Plan:</strong> {userSubscriptionStatus.plan || 'Free'}</p>
                  {userSubscriptionStatus.startDate && (
                    <p><strong>Start Date:</strong> {formatDate(userSubscriptionStatus.startDate)}</p>
                  )}
                  {userSubscriptionStatus.endDate && (
                    <p><strong>End Date:</strong> {formatDate(userSubscriptionStatus.endDate)}</p>
                  )}
                  {userSubscriptionStatus.daysRemaining !== undefined && userSubscriptionStatus.daysRemaining > 0 && (
                    <p><strong>Days Remaining:</strong> {userSubscriptionStatus.daysRemaining} days</p>
                  )}
                </div>
              ) : (
                <p>No subscription data available</p>
              )}
              
              {subscriptionError && (
                <div className="mt-2 p-2 bg-red-100 text-red-700 rounded text-sm">
                  {subscriptionError}
                </div>
              )}

              <div className="mt-4 space-y-2">
                <h4 className="font-semibold text-sm">Assign Subscription:</h4>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => handleAssignSubscription('weekly')}
                    disabled={subscriptionLoading}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm"
                  >
                    Assign 1 Week
                  </button>
                  <button
                    onClick={() => handleAssignSubscription('monthly')}
                    disabled={subscriptionLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                  >
                    Assign 1 Month
                  </button>
                  <button
                    onClick={() => handleAssignSubscription('quarterly')}
                    disabled={subscriptionLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                  >
                    Assign 3 Months
                  </button>
                  <button
                    onClick={() => handleAssignSubscription('premium')}
                    disabled={subscriptionLoading}
                    className="px-4 py-2 bg-[#006D77] text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm"
                  >
                    Assign Premium
                  </button>
                </div>
                
                {userSubscriptionStatus && userSubscriptionStatus.isActive && (
                  <button
                    onClick={handleRevokeSubscription}
                    disabled={subscriptionLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                  >
                    Revoke Subscription
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="data-sections">
            {!userHistoryLoaded ? (
              <div className="data-section">
                <p className="mb-3 text-sm" style={{ color: TEXT_DESC }}>
                  Load exam results, study sessions (lessons, vocabulary, grammar), and activity history for this user.
                </p>
                <button
                  type="button"
                  onClick={loadUserHistory}
                  disabled={userHistoryLoading}
                  className="admin-btn admin-btn-primary min-h-[48px] touch-manipulation"
                >
                  {userHistoryLoading ? 'Loading history…' : 'Load history'}
                </button>
              </div>
            ) : (
              <>
                <div className="data-section">
                  <h3 className="admin-subtitle">Recent Exam Results</h3>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Exam</th>
                          <th>Score</th>
                          <th>Percentage</th>
                          <th>Passed</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDetails.examResults.map(exam => {
                          const { score, max } = getEffectiveScore(exam);
                          const pct = score != null && max != null && max > 0 ? Math.round((score / max) * 100) : null;
                          const passed = exam.passed ?? (pct != null && pct >= 60);
                          const attempt = describeExamAttempt(exam);
                          return (
                            <tr key={exam.id}>
                              <td>{formatDate(exam.completedAt)}</td>
                              <td>
                                <span>{attempt.examLabel}</span>
                                {attempt.paper && (
                                  <span style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7 }}>{attempt.paper}</span>
                                )}
                              </td>
                              <td>{score != null && max != null ? `${score}/${max}` : ', '}</td>
                              <td>{pct != null ? `${pct}%` : ', '}</td>
                              <td>{passed ? '✅' : '❌'}</td>
                              <td>
                                <div className="flex flex-wrap gap-2 items-center">
                                  <button
                                    type="button"
                                    onClick={() => setSelectedExamDetail(exam)}
                                    className="admin-btn min-h-[44px] touch-manipulation"
                                    style={{ background: DEEP_TEAL, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: '0.875rem' }}
                                  >
                                    View full result
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteExam(selectedUser, exam.id)}
                                    className="delete-button"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {userDetails.examResults.length === 0 && (
                    <p className="text-sm py-2" style={{ color: TEXT_DESC }}>No exam results.</p>
                  )}
                </div>

                <div className="data-section">
                  <h3 className="admin-subtitle">Recent Study Sessions</h3>
                  <p className="text-sm mb-2" style={{ color: TEXT_DESC }}>What they did: grammar topics, vocabulary, lessons</p>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Title</th>
                          <th>Duration</th>
                          <th>What they did</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDetails.studySessions.map(session => {
                          const parts = [];
                          if (session.topicsStudied?.length) parts.push(`Topics: ${session.topicsStudied.slice(0, 3).join(', ')}${session.topicsStudied.length > 3 ? '…' : ''}`);
                          if (session.vocabularyLearned?.length) parts.push(`${session.vocabularyLearned.length} word(s) learned`);
                          if (session.vocabularyReviewed?.length) parts.push(`${session.vocabularyReviewed.length} reviewed`);
                          if (session.skillsWorked?.length) parts.push(`Skills: ${session.skillsWorked.slice(0, 2).join(', ')}`);
                          if (session.activityId) parts.push(session.activityId);
                          const whatTheyDid = parts.length ? parts.join(' · ') : (session.title || ', ');
                          return (
                            <tr key={session.id}>
                              <td>{formatDate(session.startTime)}</td>
                              <td>{session.type || ', '}</td>
                              <td>{session.title || ', '}</td>
                              <td>{session.duration != null && session.duration > 0 ? `${session.duration}min` : ', '}</td>
                              <td className="max-w-[200px] truncate" title={whatTheyDid}>{whatTheyDid}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {userDetails.studySessions.length === 0 && (
                    <p className="text-sm py-2" style={{ color: TEXT_DESC }}>No study sessions.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Full exam result modal, writing, section scores, user answers */}
      {selectedExamDetail && (() => {
        const exam = selectedExamDetail;
        const { score, max } = getEffectiveScore(exam);
        const pct = score != null && max != null && max > 0 ? Math.round((score / max) * 100) : null;
        const passed = exam.passed ?? (pct != null && pct >= 60);
        const rawSectionScores = exam.sectionScores && typeof exam.sectionScores === 'object' ? exam.sectionScores : {};
        // Inject writing into section scores if feedback exists but schreiben isn't already there
        const writingPoints = exam.writingFeedback?.scores?.overallScore || exam.writingFeedback?.overallScore;
        const writingAlreadyInSections = 'schreiben' in rawSectionScores || 'writing' in rawSectionScores;
        const sectionScores = (writingPoints && !writingAlreadyInSections)
          ? { ...rawSectionScores, schreiben: { score: writingPoints, maxScore: exam.writingFeedback?.guidelineMetadata?.maxPoints ?? null } }
          : rawSectionScores;
        const writingData = exam.writingData;
        const writingFeedback = exam.writingFeedback;
        const userAnswers = exam.userAnswers && typeof exam.userAnswers === 'object' ? exam.userAnswers : {};
        const writingTexts = writingData?.texts ? Object.entries(writingData.texts) : [];
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setSelectedExamDetail(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="exam-detail-title"
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-h-[90vh] w-full max-w-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0" style={{ background: DEEP_TEAL, color: '#fff' }}>
                <h2 id="exam-detail-title" className="text-lg font-semibold truncate pr-2">Full exam result</h2>
                <button
                  type="button"
                  onClick={() => setSelectedExamDetail(null)}
                  className="min-h-[48px] min-w-[48px] flex items-center justify-center rounded-lg hover:bg-white/20 touch-manipulation"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="overflow-y-auto p-4 space-y-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <p><strong>Exam:</strong> {describeExamAttempt(exam).examLabel}</p>
                  <p><strong>Paper:</strong> {describeExamAttempt(exam).paper || 'Unknown (result predates paper tracking)'}</p>
                  <p><strong>Date:</strong> {formatDate(exam.completedAt)}</p>
                  <p><strong>Score:</strong> {score != null && max != null ? `${score} / ${max}` : ', '}</p>
                  <p><strong>Percentage:</strong> {pct != null ? `${pct}%` : ', '}</p>
                  <p><strong>Passed:</strong> {passed ? '✅ Yes' : '❌ No'}</p>
                  <p><strong>Time spent:</strong> {(() => {
                    const totalSeconds = exam.timeSpent != null && exam.timeSpent > 0
                      ? exam.timeSpent
                      : (exam.sectionTimes && typeof exam.sectionTimes === 'object' && Object.keys(exam.sectionTimes).length > 0
                        ? Object.values(exam.sectionTimes).reduce((sum, s) => sum + (Number(s) || 0), 0)
                        : 0);
                    if (totalSeconds > 0) {
                      return `${Math.round(totalSeconds / 60)} min` + (exam.timeSpent == null || exam.timeSpent === 0 ? ' (from sections)' : '');
                    }
                    return '0 min (not recorded for this attempt)';
                  })()}</p>
                </div>

                {Object.keys(sectionScores).length > 0 && (
                  <div>
                    <h3 className="admin-subtitle mb-2">Section scores</h3>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Section</th>
                            <th>Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(sectionScores).map(([sectionId, value]) => {
                            let displayScore;
                            if (value == null) {
                              displayScore = ', ';
                            } else if (typeof value === 'object') {
                              const pts = value.score ?? value.points;
                              displayScore = pts != null
                                ? (value.maxScore != null ? `${pts} / ${value.maxScore}` : String(pts))
                                : ', ';
                            } else {
                              displayScore = String(value);
                            }
                            return (
                              <tr key={sectionId}>
                                <td>{sectionId}</td>
                                <td>{displayScore}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {writingTexts.length > 0 && (
                  <div>
                    <h3 className="admin-subtitle mb-2">Writing, user texts</h3>
                    <div className="space-y-4">
                      {writingTexts.map(([textKey, textEntry]) => {
                        const label = textEntry?.partName || textKey;
                        const text = typeof textEntry === 'object' && textEntry?.text != null ? textEntry.text : (typeof textEntry === 'string' ? textEntry : ', ');
                        return (
                          <div key={textKey} className="rounded-xl border border-gray-200 p-4 bg-gray-50">
                            <p className="text-xs font-semibold mb-2" style={{ color: TEXT_DESC }}>{label}</p>
                            <p className="whitespace-pre-wrap text-sm" style={{ color: TEXT_HEADING }}>{text}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {writingFeedback && (
                  <div>
                    <h3 className="admin-subtitle mb-2">Writing feedback &amp; correction</h3>
                    <div className="rounded-xl border border-purple-200 p-4 bg-purple-50/50 space-y-4 text-sm">
                      {writingFeedback.overallScore != null && (
                        <p><strong>Overall score:</strong> {writingFeedback.overallScore}{writingFeedback.guidelineMetadata?.maxPoints != null ? ` / ${writingFeedback.guidelineMetadata.maxPoints}` : ''}</p>
                      )}
                      {writingFeedback.passed != null && (
                        <p><strong>Passed:</strong> {writingFeedback.passed ? '✅' : '❌'}</p>
                      )}

                      {/* Multi-task: textCorrections (OSD B2 etc.) */}
                      {writingFeedback.textCorrections && Object.keys(writingFeedback.textCorrections).length > 0 && (
                        <div className="space-y-6">
                          {Object.entries(writingFeedback.textCorrections).map(([textKey, taskCorrection]) => {
                            const textData = writingData?.texts?.[textKey];
                            const label = textData?.partName || textKey;
                            const corrections = taskCorrection?.corrections || [];
                            const correctedText = taskCorrection?.correctedText || '';
                            const scores = taskCorrection?.scores || {};
                            const feedbackTexts = taskCorrection?.feedback || {};
                            return (
                              <div key={textKey} className="border border-purple-200 rounded-lg p-4 bg-white space-y-3">
                                <p className="font-semibold text-purple-800">{label}</p>
                                {corrections.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold mb-2" style={{ color: TEXT_DESC }}>Corrections ({corrections.length})</p>
                                    <ul className="list-disc pl-4 space-y-1 text-xs">
                                      {corrections.map((c, i) => {
                                        const orig = (textData?.text && typeof c.startIndex === 'number' && typeof c.endIndex === 'number') ? textData.text.slice(c.startIndex, c.endIndex) : (c.originalText || ', ');
                                        return (
                                          <li key={i}>
                                            <span className="line-through text-red-700">"{orig}"</span>
                                            {' → '}
                                            <span className="text-green-700 font-medium">"{c.correctedText || ''}"</span>
                                            {c.explanation && <span className="block mt-0.5" style={{ color: TEXT_DESC }}>{c.explanation}</span>}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                                {correctedText && (
                                  <div>
                                    <p className="text-xs font-semibold mb-1" style={{ color: TEXT_DESC }}>Corrected text</p>
                                    <p className="whitespace-pre-wrap p-3 bg-green-50 border border-green-200 rounded text-gray-800">{correctedText}</p>
                                  </div>
                                )}
                                {Object.keys(scores).length > 0 && <p><strong>Scores:</strong> {JSON.stringify(scores)}</p>}
                                {Object.keys(feedbackTexts).length > 0 && (
                                  <div>
                                    <p className="font-semibold mb-1">Feedback</p>
                                    {Object.entries(feedbackTexts).map(([k, v]) => <div key={k}><span className="font-medium">{k}:</span> {String(v)}</div>)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Multi-task: task1Evaluation, task2Evaluation, task3Evaluation (Goethe etc.) */}
                      {!writingFeedback.textCorrections && (writingFeedback.task1Evaluation || writingFeedback.task2Evaluation || writingFeedback.task3Evaluation) && (() => {
                        const tasks = [];
                        if (writingFeedback.task1Evaluation) tasks.push({ num: 1, data: writingFeedback.task1Evaluation });
                        if (writingFeedback.task2Evaluation) tasks.push({ num: 2, data: writingFeedback.task2Evaluation });
                        if (writingFeedback.task3Evaluation) tasks.push({ num: 3, data: writingFeedback.task3Evaluation });
                        const getTextForTask = (num) => writingData?.texts?.[`writing-task-${num}`] || Object.entries(writingData?.texts || {}).find(([, v]) => v && (v.taskIndex === num || v.partName?.includes(String(num))))?.[1];
                        return (
                          <div className="space-y-6">
                            {tasks.map(({ num, data }) => {
                              const textData = getTextForTask(num);
                              const corrections = data?.corrections || [];
                              const correctedText = data?.correctedText || '';
                              const scores = data?.scores || {};
                              const feedbackTexts = data?.feedback || {};
                              return (
                                <div key={num} className="border border-purple-200 rounded-lg p-4 bg-white space-y-3">
                                  <p className="font-semibold text-purple-800">Aufgabe {num}{textData?.partName ? `, ${textData.partName}` : ''}</p>
                                  {corrections.length > 0 && (
                                    <div>
                                      <p className="text-xs font-semibold mb-2" style={{ color: TEXT_DESC }}>Corrections ({corrections.length})</p>
                                      <ul className="list-disc pl-4 space-y-1 text-xs">
                                        {corrections.map((c, i) => {
                                          const orig = (textData?.text && typeof c.startIndex === 'number' && typeof c.endIndex === 'number') ? textData.text.slice(c.startIndex, c.endIndex) : (c.originalText || ', ');
                                          return (
                                            <li key={i}>
                                              <span className="line-through text-red-700">"{orig}"</span>
                                              {' → '}
                                              <span className="text-green-700 font-medium">"{c.correctedText || ''}"</span>
                                              {c.explanation && <span className="block mt-0.5" style={{ color: TEXT_DESC }}>{c.explanation}</span>}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                  {correctedText && (
                                    <div>
                                      <p className="text-xs font-semibold mb-1" style={{ color: TEXT_DESC }}>Corrected text</p>
                                      <p className="whitespace-pre-wrap p-3 bg-green-50 border border-green-200 rounded text-gray-800">{correctedText}</p>
                                    </div>
                                  )}
                                  {Object.keys(scores).length > 0 && <p><strong>Scores:</strong> {JSON.stringify(scores)}</p>}
                                  {Object.keys(feedbackTexts).length > 0 && (
                                    <div>
                                      <p className="font-semibold mb-1">Feedback</p>
                                      {Object.entries(feedbackTexts).map(([k, v]) => <div key={k}><span className="font-medium">{k}:</span> {String(v)}</div>)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Single-task (TELC, OSD B1): corrections + correctedText at top level */}
                      {!writingFeedback.textCorrections && !writingFeedback.task1Evaluation && !writingFeedback.task2Evaluation && !writingFeedback.task3Evaluation && (() => {
                        const corrections = writingFeedback.corrections || [];
                        const correctedText = writingFeedback.correctedText || '';
                        const scores = writingFeedback.scores || {};
                        const feedbackTexts = writingFeedback.feedback || {};
                        const textData = writingTexts.length > 0 ? writingTexts[0][1] : null;
                        if (corrections.length === 0 && !correctedText && Object.keys(scores).length === 0 && Object.keys(feedbackTexts).length === 0) return null;
                        return (
                          <div className="border border-purple-200 rounded-lg p-4 bg-white space-y-3">
                            {corrections.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold mb-2" style={{ color: TEXT_DESC }}>Corrections ({corrections.length})</p>
                                <ul className="list-disc pl-4 space-y-1 text-xs">
                                  {corrections.map((c, i) => {
                                    const orig = (textData?.text && typeof c.startIndex === 'number' && typeof c.endIndex === 'number') ? textData.text.slice(c.startIndex, c.endIndex) : (c.originalText || ', ');
                                    return (
                                      <li key={i}>
                                        <span className="line-through text-red-700">"{orig}"</span>
                                        {' → '}
                                        <span className="text-green-700 font-medium">"{c.correctedText || ''}"</span>
                                        {c.explanation && <span className="block mt-0.5" style={{ color: TEXT_DESC }}>{c.explanation}</span>}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                            {correctedText && (
                              <div>
                                <p className="text-xs font-semibold mb-1" style={{ color: TEXT_DESC }}>Corrected text</p>
                                <p className="whitespace-pre-wrap p-3 bg-green-50 border border-green-200 rounded text-gray-800">{correctedText}</p>
                              </div>
                            )}
                            {Object.keys(scores).length > 0 && <p><strong>Criteria scores:</strong> {JSON.stringify(scores)}</p>}
                            {Object.keys(feedbackTexts).length > 0 && (
                              <div>
                                <p className="font-semibold mb-1">Feedback</p>
                                {Object.entries(feedbackTexts).map(([k, v]) => <div key={k}><span className="font-medium">{k}:</span> {String(v)}</div>)}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Fallback: criteria/feedback when no per-task correction structure */}
                      {(writingFeedback.scores && Object.keys(writingFeedback.scores).length > 0 && !writingFeedback.textCorrections && !writingFeedback.task1Evaluation) && (
                        <p><strong>Criteria:</strong> {JSON.stringify(writingFeedback.scores)}</p>
                      )}
                      {writingFeedback.feedback && typeof writingFeedback.feedback === 'object' && Object.keys(writingFeedback.feedback).length > 0 && !writingFeedback.textCorrections && !writingFeedback.task1Evaluation && (
                        <div>
                          <p className="font-semibold mb-1">Feedback</p>
                          {Object.entries(writingFeedback.feedback).map(([k, v]) => (
                            <div key={k} className="mb-2"><span className="font-medium">{k}:</span> {String(v)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {Object.keys(userAnswers).length > 0 && (
                  <div>
                    <h3 className="admin-subtitle mb-2">User answers (reading/listening)</h3>
                    <div className="rounded-xl border border-gray-200 p-4 bg-gray-50 max-h-64 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap break-words" style={{ color: TEXT_HEADING }}>
                        {JSON.stringify(userAnswers, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {writingTexts.length === 0 && !writingFeedback && (
                  <div className="rounded-xl border border-amber-200 p-4 bg-amber-50/50">
                    <p className="text-sm" style={{ color: TEXT_DESC }}>
                      <strong>Writing:</strong> No writing submitted for this attempt. Writing appears here after the user completes the writing task(s) and receives feedback (e.g. via &quot;Get correction&quot; on the results page).
                    </p>
                  </div>
                )}

                {writingTexts.length === 0 && !writingFeedback && Object.keys(userAnswers).length === 0 && Object.keys(sectionScores).length === 0 && (
                  <p className="text-sm" style={{ color: TEXT_DESC }}>No section scores or answers for this result.</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Consultations Section */}
      {currentView === 'consultations' && (
        <div className="admin-consultations">
          <div className="admin-reviews-header">
            <h2 className="admin-section-title">Consultations</h2>
            <button
              type="button"
              onClick={loadConsultations}
              disabled={consultationsLoading}
              className="admin-btn admin-btn-primary min-h-[48px] touch-manipulation"
            >
              {consultationsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {consultationsLoading && consultations.length === 0 ? (
            <p className="text-sm py-4" style={{ color: TEXT_DESC }}>Loading consultations…</p>
          ) : consultations.length === 0 ? (
            <p className="text-sm py-4" style={{ color: TEXT_DESC }}>No consultations.</p>
          ) : (
            <div className="space-y-3">
              {consultations.map(c => (
                <div key={c.id} className="admin-card bg-white rounded-2xl border border-[#E1E4E8] p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold" style={{ color: TEXT_HEADING }}>{c.userName || c.userEmail || ', '}</p>
                      <p className="text-sm" style={{ color: TEXT_DESC }}>{c.userEmail}</p>
                      <p className="text-sm mt-1">
                        <strong>When:</strong> {c.date} at {c.timeSlot}
                        {c.timezone && <span className="text-xs ml-1">({c.timezone})</span>}
                      </p>
                      <p className="text-xs mt-1"><strong>Status:</strong> {c.status || 'confirmed'}</p>
                      {c.zoomJoinUrl && (
                        <a href={c.zoomJoinUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#006D77] underline mt-1 inline-block">Join Zoom</a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* GA4 Analytics Dashboard */}
      {currentView === 'analytics' && (
        <div className="admin-analytics">
          {/* Header + controls */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="admin-section-title flex items-center gap-2 mb-0">
              <BarChart3 className="w-6 h-6" />
              Analytics
            </h2>
            <div className="flex items-center gap-2">
              {/* Date range selector */}
              <div className="flex rounded-xl border border-[#E1E4E8] overflow-hidden bg-white">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setGa4Days(d)}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors touch-manipulation ${
                      ga4Days === d
                        ? 'bg-[#006D77] text-white'
                        : 'text-[#636E72] hover:bg-[#F8FAFA]'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              <button
                onClick={() => loadGA4Data(ga4Days)}
                disabled={ga4Loading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[#E1E4E8] bg-white text-sm font-medium touch-manipulation hover:border-[#006D77]/50 transition-colors disabled:opacity-50"
                style={{ color: TEXT_HEADING }}
              >
                <RefreshCw className={`w-4 h-4 ${ga4Loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Error state */}
          {ga4Error && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-6 text-sm text-red-600">
              {ga4Error}
            </div>
          )}

          {/* Loading skeleton */}
          {ga4Loading && !ga4Data && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="admin-card bg-white rounded-2xl border border-[#E1E4E8] p-5 shadow-sm animate-pulse">
                  <div className="h-4 bg-[#E1E4E8] rounded w-1/3 mb-4" />
                  <div className="h-40 bg-[#F8FAFA] rounded-xl" />
                </div>
              ))}
            </div>
          )}

          {/* Charts */}
          {ga4Data && (() => {
            const TEAL = '#006D77';
            const BLUE = '#3A86FF';
            const INDIGO = '#6366f1';
            const CORAL = '#E29578';
            const PIE_COLORS = ['#006D77', '#3A86FF', '#6366f1', '#E29578', '#83C5BE'];

            const fmtDate = (d) => {
              if (!d || d.length !== 8) return d;
              const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              return `${months[parseInt(d.slice(4,6),10)-1]} ${parseInt(d.slice(6,8),10)}`;
            };

            const shortPath = (p) => {
              if (!p || p === '/' || /^\/[a-z]{2}\/?$/.test(p)) return '🏠 Home Page';
              const stripped = p.replace(/^\/[a-z]{2}\//, '/');
              return stripped.length > 28 ? stripped.slice(0, 27) + '…' : stripped;
            };

            // Merge rows sharing the same display label after shortPath transform
            const mergeByLabel = (rows, labelKey, valKey, topN = 10) => {
              const map = new Map();
              for (const r of rows) {
                map.set(r[labelKey], (map.get(r[labelKey]) || 0) + r[valKey]);
              }
              return [...map.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, topN)
                .map(([label, val]) => ({ [labelKey]: label, [valKey]: val }))
                .reverse();
            };

            const mergeSlices = (rows) => {
              const map = new Map();
              for (const r of rows) {
                if (!r.name || r.name === '(not set)') continue;
                map.set(r.name, (map.get(r.name) || 0) + r.value);
              }
              return [...map.entries()].map(([name, value]) => ({ name, value }));
            };

            // ── Derived data ─────────────────────────────────────────────
            const kpis = ga4Data.kpis || {};
            const fmtDuration = (sec) => {
              if (!sec) return '0s';
              const m = Math.floor(sec / 60), s = sec % 60;
              return m > 0 ? `${m}m ${s}s` : `${s}s`;
            };

            const pageViews = (ga4Data.pageViewsOverTime || []).map((r) => ({
              date: fmtDate(r.dim), views: r.val,
            }));

            // pagesByUsers = pagePath × activeUsers, used for funnel + feature adoption
            const pagesByUsers = ga4Data.pagesByUsers || [];
            const findUsers = (keywords) => {
              const map = new Map();
              for (const r of pagesByUsers) {
                const sp = shortPath(r.dim);
                if (keywords.some((k) => r.dim.includes(k))) {
                  map.set(sp, (map.get(sp) || 0) + r.val);
                }
              }
              return [...map.values()].reduce((a, b) => a + b, 0);
            };

            const funnelData = [
              { stage: 'Visited site', users: kpis.activeUsers || 0 },
              { stage: 'Visited /auth', users: findUsers(['/auth']) },
              { stage: 'Reached dashboard', users: findUsers(['/dashboard']) },
              { stage: 'Used a feature', users: findUsers(['/practice', '/exam', '/lessons', '/vocabulary', '/grammar', '/redemittel']) },
              { stage: 'Viewed pricing', users: findUsers(['/pricing']) },
            ].map((s, i, arr) => ({
              ...s,
              pct: arr[0].users > 0 ? Math.round((s.users / arr[0].users) * 100) : 0,
            }));

            // Feature adoption, active users per feature section
            const FEATURES = [
              { key: '/practice', label: 'Practice' },
              { key: '/exam', label: 'Exam Simulator' },
              { key: '/lessons', label: 'Lessons' },
              { key: '/vocabulary', label: 'Vocabulary' },
              { key: '/grammar', label: 'Grammar' },
              { key: '/redemittel', label: 'Redemittel' },
            ];
            const featureAdoption = FEATURES.map(({ key, label }) => ({
              feature: label,
              users: pagesByUsers
                .filter((r) => r.dim.includes(key))
                .reduce((s, r) => s + r.val, 0),
            })).filter((f) => f.users > 0).sort((a, b) => a.users - b.users);

            const topPageViews = mergeByLabel(
              (ga4Data.topPageViews || []).map((r) => ({ page: shortPath(r.dim), views: r.val })),
              'page', 'views'
            );

            const exitPages = mergeByLabel(
              (ga4Data.exitPages || []).map((r) => ({ page: shortPath(r.dim || ', '), exits: r.val })),
              'page', 'exits'
            );

            const landingPages = mergeByLabel(
              (ga4Data.landingPages || []).map((r) => ({ page: shortPath(r.dim || ', '), visits: r.val })),
              'page', 'visits', 8
            );

            const DEVICE_LABELS = { desktop: 'Desktop', mobile: 'Mobile', tablet: 'Tablet' };
            const devices = mergeSlices(
              (ga4Data.devices || []).map((r) => ({ name: DEVICE_LABELS[r.dim] || r.dim, value: r.val }))
            );

            const NVR_LABELS = { new: 'New Users', returning: 'Returning Users' };
            const nvr = mergeSlices(
              (ga4Data.newVsReturning || [])
                .filter((r) => r.dim === 'new' || r.dim === 'returning')
                .map((r) => ({ name: NVR_LABELS[r.dim], value: r.val }))
            );

            const countries = (ga4Data.countries || [])
              .filter((r) => r.dim && r.dim !== '(not set)')
              .slice(0, 8)
              .map((r) => ({ country: r.dim, users: r.val }))
              .reverse();

            const abRows = ga4Data.abTestByVariant || [];
            const abMap = new Map();
            for (const row of abRows) {
              const variant = row.variant || 'unknown';
              if (!abMap.has(variant)) {
                abMap.set(variant, { variant, views: 0, clicks: 0, signups: 0, onboardings: 0 });
              }
              const current = abMap.get(variant);
              if (row.eventName === 'landing_experiment_viewed') current.views += row.count;
              if (row.eventName === 'landing_cta_click') current.clicks += row.count;
              if (row.eventName === 'signup_complete') current.signups += row.count;
              if (row.eventName === 'onboarding_complete') current.onboardings += row.count;
            }

            const abSummary = Array.from(abMap.values())
              .filter((r) => r.variant === 'variant_b')
              .map((r) => ({
                ...r,
                clickRate: r.views > 0 ? Math.round((r.clicks / r.views) * 1000) / 10 : 0,
                signupRate: r.views > 0 ? Math.round((r.signups / r.views) * 1000) / 10 : 0,
                onboardingRate: r.views > 0 ? Math.round((r.onboardings / r.views) * 1000) / 10 : 0,
                onboardingAfterSignupRate: r.signups > 0 ? Math.round((r.onboardings / r.signups) * 1000) / 10 : 0,
              }))
              .sort((a, b) => a.variant.localeCompare(b.variant));

            const ChartCard = ({ title, subtitle, icon: Icon, children, accent }) => (
              <div className="admin-card bg-white rounded-2xl border border-[#E1E4E8] p-5 shadow-sm">
                <div className="mb-4">
                  <p className="text-sm font-semibold flex items-center gap-2" style={{ color: TEXT_HEADING }}>
                    {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: accent || TEAL }} />}
                    {title}
                  </p>
                  {subtitle && <p className="text-xs mt-0.5" style={{ color: TEXT_DESC }}>{subtitle}</p>}
                </div>
                {children}
              </div>
            );

            const EmptyChart = ({ msg = 'No data yet for this period' }) => (
              <div className="h-32 flex items-center justify-center text-sm" style={{ color: TEXT_DESC }}>{msg}</div>
            );

            const HBar = ({ data, labelKey, valKey, color, height = 220 }) =>
              data.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={height}>
                  <BarChart data={data} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E1E4E8" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: TEXT_DESC }} allowDecimals={false} />
                    <YAxis type="category" dataKey={labelKey} width={115} tick={{ fontSize: 10, fill: TEXT_DESC }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E1E4E8' }} />
                    <Bar dataKey={valKey} fill={color} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              );

            return (
              <div className="space-y-4">

                {/* ── 1. Real KPIs ──────────────────────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'Active Users', val: (kpis.activeUsers || 0).toLocaleString(), color: TEAL, tip: 'Unique users who visited' },
                    { label: 'New Users', val: (kpis.newUsers || 0).toLocaleString(), color: BLUE, tip: 'First-time visitors' },
                    { label: 'Sessions', val: (kpis.sessions || 0).toLocaleString(), color: INDIGO, tip: 'Total visits (one user can have many)' },
                    { label: 'Bounce Rate', val: `${kpis.bounceRate || 0}%`, color: CORAL, tip: 'Left after viewing only 1 page' },
                    { label: 'Avg Session', val: fmtDuration(kpis.avgSessionDuration || 0), color: '#10b981', tip: 'Average time spent per visit' },
                    { label: 'Pages/Session', val: (kpis.pagesPerSession || 0).toFixed(1), color: '#8b5cf6', tip: 'Average pages viewed per visit' },
                  ].map(({ label, val, color, tip }) => (
                    <div key={label} className="bg-white rounded-2xl border border-[#E1E4E8] p-4 shadow-sm" title={tip}>
                      <p className="text-xs font-medium mb-1 leading-tight" style={{ color: TEXT_DESC }}>{label}</p>
                      <p className="text-xl font-bold truncate" style={{ color }}>{val}</p>
                    </div>
                  ))}
                </div>

                {/* ── 1b. Conversion Funnel, single source of truth ─────── */}
                {(() => {
                  // All funnel steps except the first use Firebase Auth / Firestore exclusively
                  // server-side counts, no ad-blocker or consent gaps.
                  // New Visitors (GA4 newUsers) is the only step that must come from GA4 because
                  // page visits cannot be counted server-side.
                  const backendFunnel = ga4Data.backendFunnel || {};
                  const newVisitors       = kpis.newUsers || 0;
                  const authSignups       = backendFunnel.authSignups || 0;
                  const profileDocs       = backendFunnel.profileDocsForSignups || 0;
                  const levelSelected     = backendFunnel.levelSelectedForSignups || 0;
                  const onboardingDone    = backendFunnel.onboardingCompleted || 0;
                  const activitySignal    = backendFunnel.activitySignalForSignups || 0;
                  const byProvider        = backendFunnel.authSignupsByProvider || {};

                  const cvr = newVisitors > 0 ? Math.round((authSignups / newVisitors) * 1000) / 10 : 0;

                  const pct = (num, denom) =>
                    denom > 0 ? Math.round((num / denom) * 100) : 0;

                  const steps = [
                    { label: 'New Visitors',     value: newVisitors,    source: 'GA4',       color: '#94a3b8', prev: null },
                    { label: 'Signed Up',         value: authSignups,    source: 'Firebase',  color: INDIGO,   prev: newVisitors },
                    { label: 'Profile Created',   value: profileDocs,    source: 'Firestore', color: TEAL,     prev: authSignups },
                    { label: 'Level Selected',    value: levelSelected,  source: 'Firestore', color: BLUE,     prev: authSignups },
                    { label: 'Onboarding Done',   value: onboardingDone, source: 'Firestore', color: '#8b5cf6', prev: authSignups },
                    { label: 'Activity Signal',   value: activitySignal, source: 'Firestore', color: '#10b981', prev: authSignups },
                  ];

                  return (
                    <div
                      className="rounded-2xl p-5 shadow-sm"
                      style={{ background: 'linear-gradient(135deg, #006D77 0%, #6366f1 100%)' }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                            Conversion Funnel
                          </p>
                          <p className="text-[11px] text-white/50 mt-0.5">
                            Firebase Auth + Firestore · exact server-side counts
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-white">{cvr}%</p>
                          <p className="text-[11px] text-white/60">Visitor → Signup CVR</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                        {steps.map(({ label, value, source, color, prev }) => {
                          const dropPct = prev !== null ? pct(value, prev) : null;
                          return (
                            <div key={label} className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                              <p className="text-[10px] font-medium text-white/50 mb-0.5 uppercase tracking-wide">{source}</p>
                              <p className="text-xs font-medium text-white/80 mb-1 leading-tight">{label}</p>
                              <p className="text-2xl font-bold text-white">{value.toLocaleString()}</p>
                              {dropPct !== null && (
                                <p className="text-[11px] mt-0.5" style={{ color: dropPct >= 80 ? '#86efac' : dropPct >= 50 ? '#fde68a' : '#fca5a5' }}>
                                  {dropPct}% of signups
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/60">
                        <span>Google {byProvider.google || 0}</span>
                        <span>·</span>
                        <span>Email {byProvider.password || 0}</span>
                        <span>·</span>
                        <span>Other {byProvider.other || 0}</span>
                        <span>·</span>
                        <span>Missing profile: {backendFunnel.missingProfileDocsForSignups || 0}</span>
                        <span>·</span>
                        <span>Missing level: {backendFunnel.missingLevelForSignups || 0}</span>
                        <span>·</span>
                        <span>No activity: {backendFunnel.noActivitySignalForSignups || 0}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Onboarding Step-by-Step Drop-off ─────────────────── */}
                {(() => {
                  const stepBreakdown = ga4Data.onboardingStepBreakdown || [];
                  const backendFunnel = ga4Data.backendFunnel || {};

                  const STEP_ORDER = [
                    'purpose_selection', 'exam_selection', 'exam_date_selection',
                    'exam_history', 'focus_areas', 'referral_source', 'placement_test_choice',
                    ...Array.from({ length: 15 }, (_, i) => `placement_question_${i + 1}`),
                    'placement_results', 'plan_selection', 'plan_generation', 'plan_ready',
                  ];
                  const STEP_LABELS = {
                    purpose_selection:      '1 · Purpose selection',
                    exam_selection:         '2 · Exam selection',
                    exam_date_selection:    '3 · Exam date',
                    exam_history:           '4 · Exam history',
                    focus_areas:            '5 · Focus areas',
                    referral_source:        '6 · Referral source',
                    placement_test_choice:  '7 · Placement test choice',
                    placement_results:      '8 · Placement results',
                    plan_selection:         '9 · Plan selection',
                    plan_generation:        '10 · Plan generation',
                    plan_ready:             '11 · Plan ready ✓',
                  };
                  for (let i = 1; i <= 15; i++) {
                    STEP_LABELS[`placement_question_${i}`] = `7.${i} · Placement Q${i}`;
                  }

                  const hasGA4Steps = stepBreakdown.length > 0;
                  const sortedSteps = hasGA4Steps
                    ? [...stepBreakdown].sort((a, b) => {
                        const ia = STEP_ORDER.indexOf(a.step);
                        const ib = STEP_ORDER.indexOf(b.step);
                        if (ia === -1 && ib === -1) return 0;
                        if (ia === -1) return 1;
                        if (ib === -1) return -1;
                        return ia - ib;
                      })
                    : [];
                  const maxStepCount = sortedSteps.length > 0 ? Math.max(...sortedSteps.map((s) => s.count), 1) : 1;

                  // Firestore-based 4-point approximation (always available)
                  const authSignups   = backendFunnel.authSignups || 0;
                  const profileDocs   = backendFunnel.profileDocsForSignups || 0;
                  const levelSelected = backendFunnel.levelSelectedForSignups || 0;
                  const onboardingDone = backendFunnel.onboardingCompleted || 0;
                  const firestoreSteps = [
                    { label: 'Signed up',       count: authSignups,    note: 'Auth account created',            color: INDIGO },
                    { label: 'Profile created', count: profileDocs,    note: '→ steps 1–6 (purpose, exam…)',    color: BLUE },
                    { label: 'Level selected',  count: levelSelected,  note: '→ placement test section',        color: TEAL },
                    { label: 'Onboarding done', count: onboardingDone, note: '→ plan generated, all 26 steps',  color: '#10b981' },
                  ].map((s, i, arr) => ({
                    ...s,
                    pct: arr[0].count > 0 ? Math.round((s.count / arr[0].count) * 100) : 0,
                  }));

                  return (
                    <ChartCard
                      title="Onboarding. Step-by-Step Drop-off"
                      subtitle={
                        hasGA4Steps
                          ? `GA4 step breakdown (${sortedSteps.length} steps tracked)`
                          : 'Firestore 4-point approximation, register customEvent:step_name in GA4 for full 26-step view'
                      }
                      icon={TrendingUp}
                      accent={CORAL}
                    >
                      {!hasGA4Steps && authSignups === 0 ? (
                        <EmptyChart msg="No onboarding data in this period" />
                      ) : hasGA4Steps ? (
                        /* Full GA4 step-by-step view */
                        <div className="space-y-1.5">
                          {sortedSteps.map((s, i) => {
                            const next = sortedSteps[i + 1];
                            const dropPct = next ? Math.round(((s.count - next.count) / Math.max(s.count, 1)) * 100) : 0;
                            const barPct = Math.round((s.count / maxStepCount) * 100);
                            const isBigLeak = dropPct > 30 && next;
                            return (
                              <div key={s.step}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>
                                    {STEP_LABELS[s.step] || s.step}
                                  </span>
                                  <span className="text-xs font-bold" style={{ color: isBigLeak ? '#ef4444' : TEAL }}>
                                    {s.count.toLocaleString()}
                                  </span>
                                </div>
                                <div className="h-4 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                  <div
                                    className="h-full rounded-lg transition-all"
                                    style={{
                                      width: `${barPct}%`,
                                      background: isBigLeak ? '#ef4444' : `linear-gradient(90deg, ${TEAL}, ${BLUE})`,
                                    }}
                                  />
                                </div>
                                {isBigLeak && (
                                  <p className="text-xs mt-0.5 font-medium" style={{ color: '#ef4444' }}>
                                    ↳ {dropPct}% drop here, friction point
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Firestore approximation */
                        <div className="space-y-0">
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 mb-3">
                            <p className="text-xs" style={{ color: '#92400e' }}>
                              Granular step data needs GA4 custom dimension <code>step_name</code> registered. Showing 4-checkpoint approximation from Firestore.
                            </p>
                          </div>
                          {firestoreSteps.map((s, i) => {
                            const next = firestoreSteps[i + 1];
                            const dropCount = next ? Math.max(0, s.count - next.count) : 0;
                            const dropStepPct = next ? Math.round((dropCount / Math.max(s.count, 1)) * 100) : 0;
                            const dropPp = next ? Math.max(0, s.pct - next.pct) : 0;
                            const isBigLeak = dropStepPct > 20 && next;
                            const leakColor = isBigLeak ? '#ef4444' : dropStepPct > 5 ? '#f59e0b' : '#94a3b8';
                            return (
                              <div key={s.label}>
                                {/* Step row */}
                                <div className="pt-2">
                                  <div className="flex items-center justify-between mb-0.5">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-xs font-semibold shrink-0" style={{ color: TEXT_HEADING }}>{s.label}</span>
                                      <span className="text-xs truncate" style={{ color: TEXT_DESC }}>{s.note}</span>
                                    </div>
                                    <span className="text-xs font-bold shrink-0 ml-2" style={{ color: s.color }}>
                                      {s.count.toLocaleString()} <span style={{ color: TEXT_DESC }}>({s.pct}% of signups)</span>
                                    </span>
                                  </div>
                                  <div className="h-5 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                    <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${s.pct}%`, background: s.color }} />
                                  </div>
                                </div>
                                {/* Drop connector, shown between this step and the next */}
                                {next && (
                                  <div className="flex items-center gap-2 pl-2 py-1">
                                    <div className="w-px h-4 shrink-0" style={{ background: leakColor }} />
                                    <p className="text-xs font-medium" style={{ color: leakColor }}>
                                      {dropCount === 0
                                        ? `↓ No drop to ${next.label}`
                                        : `↓ ${dropCount} dropped before "${next.label}", ${dropStepPct}% step loss · ${dropPp}pp of total`}
                                      {isBigLeak && ' ⚠ biggest leak'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ChartCard>
                  );
                })()}

                {/* ── Auth Drop-off: Why Visitors Don't Sign Up ─────────── */}
                {(() => {
                  const backendFunnel = ga4Data.backendFunnel || {};
                  const convEvents = ga4Data.conversionEvents || [];
                  const findEvent = (name) => (convEvents.find((e) => e.dim === name)?.val || 0);
                  const authViews      = findEvent('auth_view');
                  // Firebase Auth is the only reliable signup count. GA4 signup_complete misses
                  // OAuth users who never fire the event due to consent/blocker gaps.
                  const signupTotal    = backendFunnel.authSignups || 0;
                  const googleSignups  = backendFunnel.authSignupsByProvider?.google || 0;
                  const emailSignups   = backendFunnel.authSignupsByProvider?.password || 0;
                  const otherSignups   = backendFunnel.authSignupsByProvider?.other || 0;

                  // Conversion rate: how many who saw the auth page actually signed up
                  const authCvr = authViews > 0 ? Math.round((signupTotal / authViews) * 100) : 0;
                  // Did-not-convert: saw auth but didn't sign up
                  const didNotConvert = Math.max(0, authViews - signupTotal);

                  const diagColors = {
                    red:    { bg: '#FFF1F2', border: '#FECDD3', text: '#DC2626' },
                    orange: { bg: '#FFFBEB', border: '#FDE68A', text: '#D97706' },
                    green:  { bg: '#F0FDF4', border: '#BBF7D0', text: '#16A34A' },
                  };
                  const diagSeverity = authCvr >= 20 ? 'green' : authCvr >= 10 ? 'orange' : 'red';
                  const diagMsg = authCvr >= 20
                    ? 'Auth page conversion is healthy.'
                    : authCvr >= 10
                    ? 'Moderate conversion, room to improve trust signals or reduce friction.'
                    : 'Low conversion from auth page. Most visitors leave without signing up.';
                  const diagAction = authCvr >= 20
                    ? 'Focus on driving more visitors to /auth.'
                    : 'Make Google login the primary CTA. Add social proof above the fold.';

                  return (
                    <ChartCard
                      title="Auth Page. Signup Conversion"
                      subtitle="Firebase Auth counts (exact) · GA4 auth_view for page reach"
                      icon={LogIn}
                      accent={INDIGO}
                    >
                      {authViews === 0 ? (
                        <EmptyChart msg="No auth_view events in this period" />
                      ) : (
                        <div className="space-y-3">
                          {/* Auth page reach vs signups */}
                          {[
                            { label: 'Reached auth page', count: authViews,     desc: 'GA4 auth_view events',              color: BLUE },
                            { label: 'Signed up',         count: signupTotal,   desc: 'Firebase Auth, exact server count', color: TEAL },
                          ].map((s, i, arr) => {
                            const pct = arr[0].count > 0 ? Math.round((s.count / arr[0].count) * 100) : 0;
                            const dropped = i > 0 ? Math.max(0, arr[i - 1].count - s.count) : 0;
                            return (
                              <div key={s.label}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs font-semibold shrink-0" style={{ color: TEXT_HEADING }}>{s.label}</span>
                                    <span className="text-xs truncate" style={{ color: TEXT_DESC }}>{s.desc}</span>
                                  </div>
                                  <span className="text-xs font-bold shrink-0 ml-2" style={{ color: s.color }}>
                                    {s.count.toLocaleString()} <span style={{ color: TEXT_DESC }}>({pct}%)</span>
                                  </span>
                                </div>
                                <div className="h-5 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                  <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${pct}%`, background: s.color }} />
                                </div>
                                {dropped > 0 && (
                                  <p className="text-xs mt-0.5 font-medium" style={{ color: '#ef4444' }}>
                                    ↓ {dropped.toLocaleString()} left without signing up ({100 - pct}%)
                                  </p>
                                )}
                              </div>
                            );
                          })}

                          {/* Signup method breakdown */}
                          {signupTotal > 0 && (
                            <div className="pt-2 border-t border-[#E1E4E8]">
                              <p className="text-xs font-semibold mb-2" style={{ color: TEXT_HEADING }}>Signup method breakdown</p>
                              <div className="grid grid-cols-3 gap-2">
                                {[
                                  { label: 'Google OAuth', count: googleSignups, color: TEAL },
                                  { label: 'Email/password', count: emailSignups, color: INDIGO },
                                  { label: 'Other', count: otherSignups, color: '#94a3b8' },
                                ].map(({ label, count, color }) => (
                                  <div key={label} className="rounded-lg border border-[#E1E4E8] bg-[#F8FAFA] p-2 text-center">
                                    <p className="text-lg font-bold" style={{ color }}>{count}</p>
                                    <p className="text-[10px]" style={{ color: TEXT_DESC }}>{label}</p>
                                    <p className="text-[10px] font-medium" style={{ color: TEXT_DESC }}>
                                      {signupTotal > 0 ? Math.round((count / signupTotal) * 100) : 0}%
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div
                            className="rounded-xl p-3 border"
                            style={{ background: diagColors[diagSeverity].bg, borderColor: diagColors[diagSeverity].border }}
                          >
                            <p className="text-xs font-semibold mb-1" style={{ color: diagColors[diagSeverity].text }}>
                              Auth CVR {authCvr}%, {diagMsg}
                            </p>
                            <p className="text-xs" style={{ color: TEXT_DESC }}>{diagAction}</p>
                          </div>
                        </div>
                      )}
                    </ChartCard>
                  );
                })()}

                {/* ── 1e. Real Signup Funnel + Traffic Sources ──────────── */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Real Signup Funnel */}
                  {(() => {
                    const convEvents = ga4Data.conversionEvents || [];
                    const findEvent = (name) => (convEvents.find((e) => e.dim === name)?.val || 0);
                    const backendFunnel = ga4Data.backendFunnel || {};
                    // Use newUsers (first-time visitors only) as the funnel top, activeUsers includes
                    // returning users who already have accounts and will never convert again.
                    const newVisitors = kpis.newUsers || kpis.activeUsers || 0;
                    // Firebase Auth / Firestore are canonical for bottom-funnel steps, no tracking gaps.
                    const completedSignup = backendFunnel.authSignups > 0
                      ? backendFunnel.authSignups
                      : findEvent('signup_complete');
                    const completedOnboarding = backendFunnel.onboardingCompleted > 0
                      ? backendFunnel.onboardingCompleted
                      : findEvent('onboarding_complete');
                    // auth_view excluded from funnel: users can reach /auth directly without a CTA click,
                    // so auth_view can exceed CTA clicks and break funnel monotonicity.
                    const authViews = findEvent('auth_view');
                    const signupFunnel   = [
                      { stage: 'New visitors',          users: newVisitors,         source: 'GA4' },
                      { stage: 'Clicked CTA',           users: findEvent('landing_cta_click'), source: 'GA4' },
                      { stage: 'Completed signup',      users: completedSignup,     source: 'Firebase' },
                      { stage: 'Completed onboarding',  users: completedOnboarding, source: 'Firestore' },
                    ].map((s, i, arr) => ({
                      ...s,
                      pct: arr[0].users > 0 ? Math.round((s.users / arr[0].users) * 100) : 0,
                    }));
                    return (
                      <ChartCard
                        title="Signup Funnel"
                        subtitle="New visitors (GA4) → Signup + Onboarding (Firebase/Firestore exact)"
                        icon={LogIn}
                        accent={INDIGO}
                      >
                        {signupFunnel[0].users === 0 ? <EmptyChart msg="No event data yet" /> : (
                          <div className="space-y-2">
                            {signupFunnel.map((s, i) => {
                              const next = signupFunnel[i + 1];
                              const dropped = next ? Math.max(0, s.users - next.users) : 0;
                              const dropPct = s.users > 0 ? Math.round((dropped / s.users) * 100) : 0;
                              return (
                                <div key={s.stage}>
                                  <div className="flex items-center justify-between mb-0.5">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <span className="text-xs font-medium shrink-0" style={{ color: TEXT_HEADING }}>{s.stage}</span>
                                      <span className="text-[10px] px-1 rounded font-medium shrink-0"
                                        style={{ background: s.source === 'Firebase' ? '#ede9fe' : s.source === 'Firestore' ? '#d1fae5' : '#dbeafe', color: s.source === 'Firebase' ? '#6d28d9' : s.source === 'Firestore' ? '#065f46' : '#1e40af' }}>
                                        {s.source}
                                      </span>
                                    </div>
                                    <span className="text-xs font-bold shrink-0 ml-2" style={{ color: INDIGO }}>
                                      {s.users.toLocaleString()} ({s.pct}%)
                                    </span>
                                  </div>
                                  <div className="h-5 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                    <div
                                      className="h-full rounded-lg transition-all duration-500"
                                      style={{
                                        width: `${s.pct}%`,
                                        background: `linear-gradient(90deg, ${INDIGO}, #8b5cf6)`,
                                        opacity: 1 - i * 0.1,
                                      }}
                                    />
                                  </div>
                                  {next && dropped > 0 && (
                                    <p className="text-xs mt-0.5" style={{ color: TEXT_DESC }}>
                                      ↓ {dropped.toLocaleString()} dropped to next step ({dropPct}% lost)
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                            {authViews > 0 && (
                              <p className="text-xs pt-1 border-t border-[#E1E4E8] mt-2" style={{ color: TEXT_DESC }}>
                                Auth page views (incl. direct traffic): {authViews.toLocaleString()}, excluded from funnel as direct navigation inflates this above CTA clicks.
                              </p>
                            )}
                          </div>
                        )}
                      </ChartCard>
                    );
                  })()}

                  {/* Traffic Sources */}
                  {(() => {
                    const sources = (ga4Data.trafficSources || [])
                      .filter((r) => r.dim && r.dim !== '(not set)')
                      .map((r) => ({ channel: r.dim, users: r.val }))
                      .reverse();
                    return (
                      <ChartCard
                        title="Traffic Sources"
                        subtitle="Active users by acquisition channel"
                        icon={TrendingUp}
                        accent={BLUE}
                      >
                        <HBar data={sources} labelKey="channel" valKey="users" color={BLUE} height={200} />
                      </ChartCard>
                    );
                  })()}
                </div>

                {/* ── 1d. Retention Card ────────────────────────────────── */}
                {(() => {
                  const convEvents = ga4Data.conversionEvents || [];
                  const findEvent = (name) => (convEvents.find((e) => e.dim === name)?.val || 0);
                  const backendFunnel = ga4Data.backendFunnel || {};
                  const signupComplete = Math.max(findEvent('signup_complete'), backendFunnel.authSignups || 0);
                  const day2Return     = findEvent('day_2_return');
                  const day7Return     = findEvent('day_7_return');
                  const pricingView    = findEvent('pricing_view');
                  const planSelected   = findEvent('plan_selected');
                  const day2Rate       = signupComplete > 0 ? Math.round((day2Return / signupComplete) * 100) : 0;
                  const day7Rate       = signupComplete > 0 ? Math.round((day7Return / signupComplete) * 100) : 0;
                  return (
                    <div className="grid gap-4 md:grid-cols-2">
                      <ChartCard
                        title="Retention. Return Visits"
                        subtitle="How many signups return on Day 2 and Day 7"
                        icon={TrendingUp}
                        accent={TEAL}
                      >
                        <div className="space-y-3 mt-1">
                          {[
                            { label: 'Signups (baseline)',  val: signupComplete, color: INDIGO, pct: 100 },
                            { label: 'Returned Day 2',      val: day2Return,     color: TEAL,   pct: day2Rate },
                            { label: 'Returned Day 7',      val: day7Return,     color: BLUE,   pct: day7Rate },
                          ].map((r) => (
                            <div key={r.label}>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{r.label}</span>
                                <span className="text-xs font-bold" style={{ color: r.color }}>
                                  {r.val.toLocaleString()}
                                  {r.pct < 100 && <span style={{ color: TEXT_DESC }}> ({r.pct}%)</span>}
                                </span>
                              </div>
                              <div className="h-5 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                <div
                                  className="h-full rounded-lg transition-all duration-500"
                                  style={{ width: `${r.pct}%`, background: r.color }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </ChartCard>

                      <ChartCard
                        title="Post-Signup Intent"
                        subtitle="Pricing views and plan selections after signup"
                        icon={BarChart3}
                        accent={CORAL}
                      >
                        <div className="space-y-3 mt-1">
                          {[
                            { label: 'Pricing page views', val: pricingView,  color: CORAL },
                            { label: 'Plan selected',       val: planSelected, color: '#10b981' },
                          ].map((r) => {
                            const base = Math.max(pricingView, planSelected, 1);
                            return (
                              <div key={r.label}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{r.label}</span>
                                  <span className="text-xs font-bold" style={{ color: r.color }}>
                                    {r.val.toLocaleString()}
                                  </span>
                                </div>
                                <div className="h-5 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                  <div
                                    className="h-full rounded-lg transition-all duration-500"
                                    style={{ width: `${Math.round((r.val / base) * 100)}%`, background: r.color }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                          {signupComplete > 0 && pricingView > 0 && (
                            <p className="text-xs pt-1" style={{ color: TEXT_DESC }}>
                              {Math.round((pricingView / signupComplete) * 100)}% of signups view pricing
                              {planSelected > 0 && ` · ${Math.round((planSelected / pricingView) * 100)}% of those select a plan`}
                            </p>
                          )}
                        </div>
                      </ChartCard>
                    </div>
                  );
                })()}

                {/* ── Returning Users Monetization Gap ─────────────────── */}
                {(() => {
                  const convEvents = ga4Data.conversionEvents || [];
                  const findEvent = (name) => (convEvents.find((e) => e.dim === name)?.val || 0);
                  const backendFunnel = ga4Data.backendFunnel || {};
                  const signupComplete = Math.max(findEvent('signup_complete'), backendFunnel.authSignups || 0);
                  const onboardingDone = backendFunnel.onboardingCompleted || 0;
                  const pricingView   = findEvent('pricing_view');
                  const planSelected  = findEvent('plan_selected');
                  const purchases     = findEvent('purchase');
                  const day7Return    = findEvent('day_7_return');
                  const paywallShown  = findEvent('paywall_shown');
                  const paywallByContext = (ga4Data.paywallByContext || []).map((r) => ({
                    context: r.context,
                    count: r.count,
                    rate: paywallShown > 0 ? Math.round((r.count / paywallShown) * 100) : 0,
                  }));

                  // Monetization gap: use actual purchase events (not plan_selected, which includes free)
                  const returningEngaged = day7Return;
                  const pricingRate = returningEngaged > 0 ? Math.round((pricingView / Math.max(returningEngaged, onboardingDone, 1)) * 100) : 0;
                  const checkoutConvRate = pricingView > 0 ? Math.round((purchases / pricingView) * 100) : 0;
                  const monetizationGap = onboardingDone > 0 && purchases < onboardingDone * 0.05;

                  if (signupComplete === 0 && onboardingDone === 0) return null;

                  return (
                    <ChartCard
                      title="Returning Users. Why They Don't Pay"
                      subtitle="Monetization gap analysis for engaged but non-paying users"
                      icon={TrendingUp}
                      accent={CORAL}
                    >
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { label: 'Onboarded users', val: onboardingDone, color: TEAL, tip: 'Completed signup + onboarding' },
                            { label: 'Saw pricing page', val: pricingView, color: CORAL, tip: 'pricing_view events' },
                            { label: 'Paywall shown', val: paywallShown, color: '#f59e0b', tip: 'paywall_shown events, free users hitting the upgrade gate (exam results, lessons, Lesen, Schreiben, Hören)' },
                            { label: 'Purchased', val: purchases, color: '#10b981', tip: 'purchase events, paid only, excludes free plan selections' },
                          ].map(({ label, val, color, tip }) => (
                            <div key={label} className="rounded-xl border border-[#E1E4E8] bg-[#F8FAFA] p-3 text-center" title={tip}>
                              <p className="text-xs mb-1 leading-tight" style={{ color: TEXT_DESC }}>{label}</p>
                              <p className="text-lg font-bold" style={{ color }}>{val}</p>
                            </div>
                          ))}
                        </div>
                        {paywallShown > 0 && (
                          <div className="rounded-xl border border-[#E1E4E8] bg-[#F8FAFA] p-3 space-y-2">
                            <p className="text-xs font-semibold" style={{ color: TEXT_DESC }}>
                              Paywall funnel: {paywallShown} exposures → {purchases} purchases
                              {' '}({Math.round((purchases / paywallShown) * 100)}% paywall→purchase rate)
                            </p>
                            {paywallByContext.length > 0 ? (
                              <div className="space-y-1.5 pt-1">
                                <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: TEXT_DESC }}>Exposures by surface</p>
                                {paywallByContext.map(({ context, count, rate }) => (
                                  <div key={context} className="flex items-center gap-2">
                                    <span className="text-xs w-20 flex-shrink-0 capitalize" style={{ color: TEXT_DESC }}>{context}</span>
                                    <div className="flex-1 h-2 rounded-full bg-black/8 overflow-hidden">
                                      <div
                                        className="h-full rounded-full"
                                        style={{ width: `${rate}%`, backgroundColor: '#f59e0b' }}
                                      />
                                    </div>
                                    <span className="text-xs w-8 text-right font-semibold" style={{ color: TEXT_HEADING }}>{count}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs" style={{ color: TEXT_DESC }}>
                                Per-context breakdown not yet available. Register <code>context</code> as a GA4 custom dimension (scope: Event, parameter: <code>context</code>) for the <code>paywall_shown</code> event to enable this chart.
                              </p>
                            )}
                          </div>
                        )}
                        {planSelected > purchases && (
                          <p className="text-xs" style={{ color: TEXT_DESC }}>
                            {planSelected} plan selections total · {planSelected - purchases} selected free plan · {purchases} paid
                          </p>
                        )}

                        {monetizationGap && (
                          <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
                            <p className="text-xs font-semibold" style={{ color: '#DC2626' }}>
                              Monetization gap: {onboardingDone} users completed onboarding but only {purchases} purchased ({onboardingDone > 0 ? Math.round((purchases / onboardingDone) * 100) : 0}% paid conversion).
                            </p>
                            <p className="text-xs font-medium" style={{ color: '#DC2626' }}>Most likely causes:</p>
                            <ul className="text-xs space-y-1.5 list-disc list-inside" style={{ color: TEXT_DESC }}>
                              <li><strong>Free tier gives too much access.</strong> Users complete onboarding selecting the "free" plan and never hit a paywall. They return repeatedly for free.</li>
                              <li><strong>Post-onboarding paywall.</strong> Contextual upgrade modals exist on exam results (1st attempt), lesson exit, Lesen, Schreiben, and Hören probe gates. Check <code>paywall_shown</code> count above, if it's zero, the gates aren't firing.</li>
                              <li><strong>Pricing page not shown to returning users.</strong> {pricingRate}% of retained users ever saw the pricing page, most never get a chance to convert.</li>
                              <li><strong>Price/value mismatch.</strong> Users who do see pricing don't convert ({checkoutConvRate}% pricing → purchase). They may be students or may not trust the product yet.</li>
                            </ul>
                            <p className="text-xs font-medium mt-1" style={{ color: '#DC2626' }}>Quick wins:</p>
                            <ul className="text-xs space-y-1 list-disc list-inside" style={{ color: TEXT_DESC }}>
                              <li>Gate 1 high-value feature (e.g. full exam simulator or AI writing feedback) after 3 free uses</li>
                              <li>Show an upgrade banner to users who have visited 5+ times without paying</li>
                              <li>Add a "Try Premium for 3 days" trial CTA in the dashboard after onboarding</li>
                            </ul>
                          </div>
                        )}

                        {!monetizationGap && purchases > 0 && (
                          <p className="text-xs rounded-xl border border-[#E1E4E8] bg-[#F8FAFA] p-3" style={{ color: TEXT_DESC }}>
                            Monetization healthy, {Math.round((purchases / Math.max(onboardingDone, 1)) * 100)}% of onboarded users purchased ({purchases} paid of {onboardingDone} onboarded).
                          </p>
                        )}
                      </div>
                    </ChartCard>
                  );
                })()}

                {/* ── 2. Activation Funnel ──────────────────────────────── */}
                <ChartCard
                  title="Activation Funnel. Active Users"
                  subtitle="How many users reach each stage of the app"
                  icon={LogIn}
                >
                  {funnelData[0].users === 0 ? <EmptyChart /> : (
                    <div className="space-y-2">
                      {funnelData.map((s, i) => (
                        <div key={s.stage}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{s.stage}</span>
                            <span className="text-xs font-bold" style={{ color: TEAL }}>{s.users} users ({s.pct}%)</span>
                          </div>
                          <div className="h-6 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                            <div
                              className="h-full rounded-lg transition-all duration-500"
                              style={{
                                width: `${s.pct}%`,
                                background: `linear-gradient(90deg, ${TEAL}, ${BLUE})`,
                                opacity: 1 - i * 0.12,
                              }}
                            />
                          </div>
                          {i < funnelData.length - 1 && funnelData[i+1].users > 0 && (
                            <p className="text-xs mt-0.5" style={{ color: TEXT_DESC }}>
                              Drop-off: {s.users - funnelData[i+1].users} users ({100 - Math.round((funnelData[i+1].users / Math.max(s.users,1)) * 100)}% lost)
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ChartCard>

                {/* ── 3. Feature Adoption ───────────────────────────────── */}
                <ChartCard
                  title="Feature Adoption. Active Users per Feature"
                  subtitle="Which parts of the app users actually use"
                  icon={BarChart3}
                >
                  {featureAdoption.length === 0
                    ? <EmptyChart msg="No feature usage data yet" />
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(160, featureAdoption.length * 38)}>
                        <BarChart data={featureAdoption} layout="vertical" margin={{ top: 0, right: 40, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E1E4E8" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: TEXT_DESC }} allowDecimals={false} />
                          <YAxis type="category" dataKey="feature" width={100} tick={{ fontSize: 11, fill: TEXT_DESC }} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E1E4E8' }} />
                          <Bar dataKey="users" fill={INDIGO} radius={[0, 4, 4, 0]}
                            label={{ position: 'right', fontSize: 11, fill: TEXT_DESC }} />
                        </BarChart>
                      </ResponsiveContainer>
                    )
                  }
                </ChartCard>

                {/* ── Post-Onboarding: First & Last Actions ────────────── */}
                {(() => {
                  const APP_PATHS = ['/practice', '/exam', '/lessons', '/vocabulary', '/grammar', '/redemittel', '/dashboard', '/results', '/plan'];

                  // First actions: feature adoption (what logged-in users actually use)
                  // featureAdoption is already computed above, reuse it
                  const firstActions = [...featureAdoption].sort((a, b) => b.users - a.users).slice(0, 6);
                  const maxFirst = firstActions.length > 0 ? Math.max(...firstActions.map((f) => f.users), 1) : 1;

                  // Last actions: session_last_page filtered to authenticated app paths only
                  // exitPages is the already-merged version; re-filter from raw for app paths
                  const appExits = mergeByLabel(
                    (ga4Data.exitPages || [])
                      .filter((r) => APP_PATHS.some((p) => (r.dim || '').includes(p)))
                      .map((r) => ({ page: shortPath(r.dim || ', '), exits: r.val })),
                    'page', 'exits', 8
                  ).reverse();
                  const maxExits = appExits.length > 0 ? Math.max(...appExits.map((e) => e.exits), 1) : 1;

                  // Journey summary from conversionEvents
                  const convEvents = ga4Data.conversionEvents || [];
                  const journeySteps = convEvents.find((e) => e.dim === 'onboarding_step_completed')?.val || 0;
                  const backendFunnel = ga4Data.backendFunnel || {};
                  const onboardingUsers = backendFunnel.onboardingCompleted || 0;
                  const avgJourneySteps = onboardingUsers > 0 ? (journeySteps / onboardingUsers).toFixed(1) : ', ';

                  // post_login_landing count, total returns to the app by logged-in users
                  const postLoginCount = convEvents.find((e) => e.dim === 'post_login_landing')?.val ||
                    (ga4Data.landingPages || []).reduce((s, r) => s + r.val, 0);

                  return (
                    <div className="space-y-4">
                      {/* Summary strip */}
                      {onboardingUsers > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { label: 'Onboarded users', val: onboardingUsers.toLocaleString(), color: TEAL, tip: 'Completed all onboarding steps' },
                            { label: 'Avg onboarding steps', val: avgJourneySteps, color: INDIGO, tip: 'onboarding_step_completed events / onboarded users' },
                            { label: 'App re-logins', val: postLoginCount > 0 ? postLoginCount.toLocaleString() : ', ', color: BLUE, tip: 'Total post_login_landing events (return visits)' },
                          ].map(({ label, val, color, tip }) => (
                            <div key={label} className="bg-white rounded-xl border border-[#E1E4E8] p-3 shadow-sm text-center" title={tip}>
                              <p className="text-xs mb-1 leading-tight" style={{ color: TEXT_DESC }}>{label}</p>
                              <p className="text-lg font-bold" style={{ color }}>{val}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="grid gap-4 md:grid-cols-2">
                        {/* First actions */}
                        <ChartCard
                          title="Post-Onboarding: First Features Used"
                          subtitle="Features active (logged-in) users actually open"
                          icon={MousePointerClick}
                          accent={TEAL}
                        >
                          {firstActions.length === 0 ? (
                            <EmptyChart msg="No feature usage data yet" />
                          ) : (
                            <div className="space-y-2">
                              {firstActions.map((f) => {
                                const pct = Math.round((f.users / maxFirst) * 100);
                                return (
                                  <div key={f.feature}>
                                    <div className="flex items-center justify-between mb-0.5">
                                      <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{f.feature}</span>
                                      <span className="text-xs font-bold" style={{ color: TEAL }}>{f.users} users</span>
                                    </div>
                                    <div className="h-4 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                      <div
                                        className="h-full rounded-lg"
                                        style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${TEAL}, ${BLUE})` }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                              {onboardingUsers > 0 && firstActions[0] && (
                                <p className="text-xs pt-1 border-t border-[#E1E4E8]" style={{ color: TEXT_DESC }}>
                                  {Math.round((firstActions[0].users / onboardingUsers) * 100)}% of onboarded users tried {firstActions[0].feature}
                                </p>
                              )}
                            </div>
                          )}
                        </ChartCard>

                        {/* Last actions */}
                        <ChartCard
                          title="Post-Onboarding: Last Page Before Leaving"
                          subtitle="Where active users exit the app (authenticated paths only)"
                          icon={MousePointerClick}
                          accent={CORAL}
                        >
                          {appExits.length === 0 ? (
                            <EmptyChart msg="No exit data for authenticated paths yet" />
                          ) : (
                            <div className="space-y-2">
                              {appExits.map((e) => {
                                const pct = Math.round((e.exits / maxExits) * 100);
                                return (
                                  <div key={e.page}>
                                    <div className="flex items-center justify-between mb-0.5">
                                      <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{e.page}</span>
                                      <span className="text-xs font-bold" style={{ color: CORAL }}>{e.exits} exits</span>
                                    </div>
                                    <div className="h-4 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                      <div className="h-full rounded-lg" style={{ width: `${pct}%`, background: CORAL }} />
                                    </div>
                                  </div>
                                );
                              })}
                              <p className="text-xs pt-1 border-t border-[#E1E4E8]" style={{ color: TEXT_DESC }}>
                                These are the last pages users visit in a session, high exits on a single feature may mean they finished a task or hit a wall.
                              </p>
                            </div>
                          )}
                        </ChartCard>
                      </div>
                    </div>
                  );
                })()}

                {/* ── 4. Page views over time ───────────────────────────── */}
                <ChartCard title={`Page Views. Last ${ga4Days} Days`} icon={TrendingUp}>
                  {pageViews.length === 0 ? <EmptyChart /> : (
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={pageViews} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E1E4E8" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: TEXT_DESC }}
                          interval={Math.max(0, Math.floor(pageViews.length / 7) - 1)} />
                        <YAxis tick={{ fontSize: 10, fill: TEXT_DESC }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E1E4E8' }} />
                        <Line type="monotone" dataKey="views" stroke={TEAL} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>

                {/* ── 5. Landing A/B conversion summary ─────────────────── */}
                <ChartCard
                  title="Landing A/B Summary (landing_page_v1)"
                  subtitle="By variant: views, CTA clicks, signups, and onboarding completions"
                  icon={BarChart3}
                  accent={INDIGO}
                >
                  {abSummary.length === 0 ? (
                    <div className="text-sm" style={{ color: TEXT_DESC }}>
                      No A/B rows yet. If traffic exists, register GA4 custom dimensions for
                      event params `experiment_id` and `experiment_variant` and wait for fresh events.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-[#E1E4E8]" style={{ color: TEXT_DESC }}>
                            <th className="py-2 pr-3">Variant</th>
                            <th className="py-2 pr-3">Views</th>
                            <th className="py-2 pr-3">CTA Clicks</th>
                            <th className="py-2 pr-3">Signups</th>
                            <th className="py-2 pr-3">Onboardings</th>
                            <th className="py-2 pr-3">CTR</th>
                            <th className="py-2">Signup Rate</th>
                            <th className="py-2">Onboarding Rate</th>
                            <th className="py-2">Onboarding/Signup</th>
                          </tr>
                        </thead>
                        <tbody>
                          {abSummary.map((row) => (
                            <tr key={row.variant} className="border-b border-[#F1F5F9]" style={{ color: TEXT_HEADING }}>
                              <td className="py-2 pr-3 font-semibold">{row.variant}</td>
                              <td className="py-2 pr-3">{row.views.toLocaleString()}</td>
                              <td className="py-2 pr-3">{row.clicks.toLocaleString()}</td>
                              <td className="py-2 pr-3">{row.signups.toLocaleString()}</td>
                              <td className="py-2 pr-3">{row.onboardings.toLocaleString()}</td>
                              <td className="py-2 pr-3">{row.clickRate}%</td>
                              <td className="py-2">{row.signupRate}%</td>
                              <td className="py-2">{row.onboardingRate}%</td>
                              <td className="py-2">{row.onboardingAfterSignupRate}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </ChartCard>

                {/* ── 5b. Landing Page Engagement (scroll depth + time) ── */}
                {(() => {
                  const fs = landingStats?.scroll || {};
                  const ft = landingStats?.time || {};

                  // ── Scroll depth: prefer GA4 event data, fall back to Firestore counters.
                  // Note: Firestore counters had a bug where total_sessions only counted sessions
                  // that reached the 25% scroll milestone (fixed in analytics.js, but historical
                  // data may be zero). GA4 data requires customEvent:depth_pct custom dimension.
                  const ga4Scroll = ga4Data.ga4ScrollDepth || [];
                  const ga4TotalSessions = ga4Data.ga4LandingTotalSessions || 0;
                  const hasGA4Scroll = ga4Scroll.length > 0;

                  // Map GA4 depth_pct events to milestone counts
                  const ga4ByDepth = {};
                  for (const r of ga4Scroll) ga4ByDepth[r.depth] = r.count;

                  const fsTotalSessions = fs.total_sessions || 0;
                  const totalSessions = hasGA4Scroll ? ga4TotalSessions : fsTotalSessions;
                  const dataSource = hasGA4Scroll ? 'GA4' : (fsTotalSessions > 0 ? 'Firestore' : null);

                  const scrollFunnel = [
                    { label: 'Page loaded',   count: totalSessions },
                    { label: 'Scrolled 25%',  count: hasGA4Scroll ? (ga4ByDepth[25] || 0) : (fs.reached_25 || 0) },
                    { label: 'Scrolled 50%',  count: hasGA4Scroll ? (ga4ByDepth[50] || 0) : (fs.reached_50 || 0) },
                    { label: 'Scrolled 75%',  count: hasGA4Scroll ? (ga4ByDepth[75] || 0) : (fs.reached_75 || 0) },
                    { label: 'Scrolled 90%',  count: hasGA4Scroll ? (ga4ByDepth[90] || 0) : (fs.reached_90 || 0) },
                  ].map((s, i, arr) => ({
                    ...s,
                    pct: arr[0].count > 0 ? Math.round((s.count / arr[0].count) * 100) : 0,
                  }));

                  // ── Time on page: prefer GA4 event data, fall back to Firestore
                  const ga4Time = ga4Data.ga4TimeBuckets || [];
                  const hasGA4Time = ga4Time.length > 0;
                  const ga4TimeByBucket = {};
                  for (const r of ga4Time) ga4TimeByBucket[r.bucket] = r.count;

                  const TIME_LABELS = {
                    lt_10s: '< 10 sec',
                    '10_30s': '10–30 sec',
                    '30_60s': '30–60 sec',
                    '1_2min': '1–2 min',
                    '2_5min': '2–5 min',
                    gt_5min: '5 min+',
                  };
                  const timeTotalSessions = hasGA4Time
                    ? ga4Time.reduce((s, r) => s + r.count, 0)
                    : (ft.total || 0);
                  const timeBuckets = Object.entries(TIME_LABELS).map(([key, label]) => {
                    const count = hasGA4Time ? (ga4TimeByBucket[key] || 0) : (ft[key] || 0);
                    return {
                      label, count,
                      pct: timeTotalSessions > 0 ? Math.round((count / timeTotalSessions) * 100) : 0,
                    };
                  });
                  const maxTimeBucket = Math.max(...timeBuckets.map((b) => b.count), 1);

                  return (
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Scroll depth funnel */}
                      <ChartCard
                        title="Landing Page. Scroll Depth"
                        subtitle={
                          totalSessions > 0
                            ? `${totalSessions.toLocaleString()} sessions · source: ${dataSource}`
                            : 'No data yet, register customEvent:depth_pct in GA4 or deploy the tracking fix'
                        }
                        icon={TrendingUp}
                        accent={TEAL}
                      >
                        {totalSessions === 0 ? (
                          <div className="space-y-3">
                            <EmptyChart msg="No scroll depth data yet" />
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2">
                              <p className="text-xs" style={{ color: '#92400e' }}>
                                Two things needed: (1) deploy the analytics.js fix so sessions are counted on page load; (2) register <code>customEvent:depth_pct</code> as a GA4 custom dimension for GA4-based data. Firestore data will appear after the next deploy.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {scrollFunnel.map((s, i) => (
                              <div key={s.label}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{s.label}</span>
                                  <span className="text-xs font-bold" style={{ color: TEAL }}>
                                    {s.count.toLocaleString()} ({s.pct}%)
                                  </span>
                                </div>
                                <div className="h-5 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                  <div
                                    className="h-full rounded-lg transition-all duration-500"
                                    style={{
                                      width: `${s.pct}%`,
                                      background: `linear-gradient(90deg, ${TEAL}, #3A86FF)`,
                                      opacity: 1 - i * 0.12,
                                    }}
                                  />
                                </div>
                                {i > 0 && i < scrollFunnel.length - 1 && scrollFunnel[i + 1].count > 0 && (
                                  <p className="text-xs mt-0.5" style={{ color: TEXT_DESC }}>
                                    Drop-off after this point: {s.count - scrollFunnel[i + 1].count} ({100 - Math.round((scrollFunnel[i + 1].count / Math.max(s.count, 1)) * 100)}%)
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </ChartCard>

                      {/* Time on page distribution */}
                      <ChartCard
                        title="Landing Page. Time on Page"
                        subtitle={
                          timeTotalSessions > 0
                            ? `${timeTotalSessions.toLocaleString()} sessions · source: ${hasGA4Time ? 'GA4' : 'Firestore'}`
                            : 'No data yet, register customEvent:time_bucket in GA4 or wait for first visitor'
                        }
                        icon={TrendingUp}
                        accent={INDIGO}
                      >
                        {timeTotalSessions === 0 ? (
                          <EmptyChart msg="No time-on-page data yet" />
                        ) : (
                          <div className="space-y-2">
                            {timeBuckets.map((b) => (
                              <div key={b.label}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{b.label}</span>
                                  <span className="text-xs font-bold" style={{ color: INDIGO }}>
                                    {b.count.toLocaleString()} ({b.pct}%)
                                  </span>
                                </div>
                                <div className="h-5 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                  <div
                                    className="h-full rounded-lg transition-all duration-500"
                                    style={{
                                      width: `${Math.round((b.count / maxTimeBucket) * 100)}%`,
                                      background: `linear-gradient(90deg, ${INDIGO}, #8b5cf6)`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ChartCard>
                    </div>
                  );
                })()}

                {/* ── Landing Page Exit Diagnosis ───────────────────────── */}
                {(() => {
                  // Use GA4 scroll data if available, otherwise Firestore
                  const fs = landingStats?.scroll || {};
                  const ga4Scroll = ga4Data.ga4ScrollDepth || [];
                  const hasGA4Scroll = ga4Scroll.length > 0;
                  const ga4ByDepth = {};
                  for (const r of ga4Scroll) ga4ByDepth[r.depth] = r.count;
                  const fsTotalSessions = fs.total_sessions || 0;
                  const ga4TotalSessions = ga4Data.ga4LandingTotalSessions || 0;
                  const totalSessions = hasGA4Scroll ? ga4TotalSessions : fsTotalSessions;
                  const r25 = hasGA4Scroll ? (ga4ByDepth[25] || 0) : (fs.reached_25 || 0);
                  const r50 = hasGA4Scroll ? (ga4ByDepth[50] || 0) : (fs.reached_50 || 0);
                  const r75 = hasGA4Scroll ? (ga4ByDepth[75] || 0) : (fs.reached_75 || 0);
                  const r90 = hasGA4Scroll ? (ga4ByDepth[90] || 0) : (fs.reached_90 || 0);

                  // How many exits happened directly FROM the landing/home page?
                  // exitPages is already merged, '🏠 Home Page' is the home path label.
                  const totalExitCount = exitPages.reduce((s, e) => s + e.exits, 0);
                  const homeExits = exitPages.find((e) => e.page === '🏠 Home Page')?.exits || 0;
                  const homeExitRate = totalExitCount > 0 ? Math.round((homeExits / totalExitCount) * 100) : 0;

                  // Scroll depth breakdown as "how far they got before leaving"
                  const scrollPoints = [
                    { label: 'Loaded page',    count: totalSessions, zone: 'Above the fold only' },
                    { label: 'Scrolled 25%',   count: r25,           zone: 'Hero + intro visible' },
                    { label: 'Scrolled 50%',   count: r50,           zone: 'Features section' },
                    { label: 'Scrolled 75%',   count: r75,           zone: 'Pricing / testimonials' },
                    { label: 'Scrolled 90%',   count: r90,           zone: 'Footer / CTA visible' },
                  ].map((s, i, arr) => ({
                    ...s,
                    pct: arr[0].count > 0 ? Math.round((s.count / arr[0].count) * 100) : 0,
                    dropped: i > 0 ? (arr[i - 1].count - s.count) : 0,
                  }));

                  // Find biggest drop zone
                  const biggestDropZone = scrollPoints.slice(1).reduce(
                    (best, cur) => (cur.dropped > best.dropped ? cur : best),
                    scrollPoints[1] || { dropped: 0, zone: '' }
                  );

                  const diagnosis = (() => {
                    if (totalSessions === 0) return null;
                    const pct25 = scrollPoints[1]?.pct || 0;
                    const pct50 = scrollPoints[2]?.pct || 0;
                    if (pct25 < 50) return {
                      msg: `${100 - pct25}% of visitors leave above the fold, the hero section doesn't answer "what is this for me?"`,
                      action: 'Rewrite the headline to name the pain (e.g. "Pass TELC B2 in 8 weeks"). Add a visible subheadline and CTA above the scroll.',
                      severity: 'red',
                    };
                    if (pct50 < 50) return {
                      msg: `Most visitors see the hero but leave before the features section (50% scroll). They understand the concept but aren't convinced.`,
                      action: 'Move 1-2 social proof elements (user count, exam pass rate) above the 50% scroll mark.',
                      severity: 'orange',
                    };
                    return {
                      msg: 'Most visitors scroll past 50%, content is engaging. Focus on CTA conversion at the 75–90% zone.',
                      action: 'Add a sticky or repeated CTA button at the 75% scroll position.',
                      severity: 'green',
                    };
                  })();

                  const diagColors = {
                    red:    { bg: '#FFF1F2', border: '#FECDD3', text: '#DC2626' },
                    orange: { bg: '#FFFBEB', border: '#FDE68A', text: '#D97706' },
                    green:  { bg: '#F0FDF4', border: '#BBF7D0', text: '#16A34A' },
                  };

                  return (
                    <ChartCard
                      title="Landing Page. Where Users Give Up"
                      subtitle="How far visitors read before leaving + what % of total exits came from the landing page"
                      icon={TrendingUp}
                      accent={CORAL}
                    >
                      {totalSessions === 0 && homeExits === 0 ? (
                        <EmptyChart msg="No landing page data yet for this period" />
                      ) : (
                        <div className="space-y-4">
                          {/* Exit rate stat */}
                          {totalExitCount > 0 && (
                            <div className="flex gap-3">
                              <div className="flex-1 rounded-xl border border-[#E1E4E8] bg-[#F8FAFA] p-3 text-center">
                                <p className="text-xs mb-1" style={{ color: TEXT_DESC }}>Exits FROM landing page</p>
                                <p className="text-2xl font-bold" style={{ color: homeExitRate > 50 ? '#ef4444' : TEAL }}>{homeExits.toLocaleString()}</p>
                                <p className="text-xs font-medium" style={{ color: TEXT_DESC }}>{homeExitRate}% of all exits</p>
                              </div>
                              <div className="flex-1 rounded-xl border border-[#E1E4E8] bg-[#F8FAFA] p-3 text-center">
                                <p className="text-xs mb-1" style={{ color: TEXT_DESC }}>Total tracked sessions</p>
                                <p className="text-2xl font-bold" style={{ color: INDIGO }}>{totalSessions.toLocaleString()}</p>
                                <p className="text-xs font-medium" style={{ color: TEXT_DESC }}>on landing page</p>
                              </div>
                            </div>
                          )}

                          {/* Scroll depth as "what they saw before leaving" */}
                          {totalSessions > 0 && (
                            <div className="space-y-1.5">
                              <p className="text-xs font-semibold" style={{ color: TEXT_HEADING }}>How far they scrolled before leaving:</p>
                              {scrollPoints.map((s, i) => (
                                <div key={s.label}>
                                  <div className="flex items-center justify-between mb-0.5">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium" style={{ color: TEXT_HEADING }}>{s.label}</span>
                                      <span className="text-xs" style={{ color: TEXT_DESC }}>{s.zone}</span>
                                    </div>
                                    <span className="text-xs font-bold" style={{ color: i === 0 ? INDIGO : s.pct < 50 ? '#ef4444' : TEAL }}>
                                      {s.count.toLocaleString()} ({s.pct}%)
                                    </span>
                                  </div>
                                  <div className="h-4 bg-[#F8FAFA] rounded-lg overflow-hidden border border-[#E1E4E8]">
                                    <div
                                      className="h-full rounded-lg transition-all duration-500"
                                      style={{
                                        width: `${s.pct}%`,
                                        background: s.pct < 50 && i > 0
                                          ? 'linear-gradient(90deg, #ef4444, #f97316)'
                                          : `linear-gradient(90deg, ${TEAL}, ${BLUE})`,
                                        opacity: 1 - i * 0.08,
                                      }}
                                    />
                                  </div>
                                  {i > 0 && s.dropped > 0 && (
                                    <p className="text-xs mt-0.5" style={{ color: TEXT_DESC }}>
                                      {s.dropped.toLocaleString()} left before reaching this point
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Diagnosis */}
                          {diagnosis && (
                            <div
                              className="rounded-xl p-3 border"
                              style={{ background: diagColors[diagnosis.severity].bg, borderColor: diagColors[diagnosis.severity].border }}
                            >
                              <p className="text-xs font-semibold mb-1" style={{ color: diagColors[diagnosis.severity].text }}>
                                Diagnosis: {diagnosis.msg}
                              </p>
                              <p className="text-xs" style={{ color: TEXT_DESC }}>Action: {diagnosis.action}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </ChartCard>
                  );
                })()}

                {/* ── 6. Top pages + Exit pages ─────────────────────────── */}
                <div className="grid gap-4 md:grid-cols-2">
                  <ChartCard title="Top Pages by Views" icon={BarChart3} accent={BLUE}>
                    <HBar data={topPageViews} labelKey="page" valKey="views" color={BLUE} />
                  </ChartCard>
                  <ChartCard
                    title="Exit Pages. Where Users Leave"
                    subtitle="Last page before closing the tab"
                    icon={MousePointerClick}
                    accent={CORAL}
                  >
                    <HBar data={exitPages} labelKey="page" valKey="exits" color={CORAL} />
                  </ChartCard>
                </div>

                {/* ── 7. Post-login landing + Countries ────────────────── */}
                <div className="grid gap-4 md:grid-cols-2">
                  <ChartCard
                    title="First Page After Login"
                    subtitle="Where users land immediately after signing in"
                    icon={LogIn}
                  >
                    <HBar data={landingPages} labelKey="page" valKey="visits" color={TEAL} height={200} />
                  </ChartCard>
                  <ChartCard title="Users by Country" icon={Users} accent={BLUE}>
                    <HBar data={countries} labelKey="country" valKey="users" color={BLUE} height={200} />
                  </ChartCard>
                </div>

                {/* ── 8. Devices + New vs Returning ────────────────────── */}
                <div className="grid gap-4 md:grid-cols-2">
                  {[
                    { title: 'Devices (Active Users)', data: devices },
                    { title: 'New vs Returning (Active Users)', data: nvr },
                  ].map(({ title, data }) => (
                    <ChartCard key={title} title={title}>
                      {data.length === 0 ? <EmptyChart /> : (
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={data} dataKey="value" nameKey="name"
                              cx="50%" cy="50%" outerRadius={65} innerRadius={35}
                              label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}
                              labelLine={false}
                            >
                              {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E1E4E8' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </ChartCard>
                  ))}
                </div>

                <div className="flex justify-end">
                  <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm hover:underline" style={{ color: TEAL }}>
                    Open Google Analytics <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Onboarding Analytics Section */}
      {currentView === 'survey' && (
        <React.Suspense fallback={<p className="text-sm text-black/50">Loading survey results…</p>}>
          <SurveyResultsView />
        </React.Suspense>
      )}

      {currentView === 'corrections' && (
        <React.Suspense fallback={<p className="text-sm text-black/50">Loading corrections…</p>}>
          <TeacherCorrectionsView />
        </React.Suspense>
      )}

      {currentView === 'onboarding' && (
        <div className="admin-analytics space-y-6">
          <h2 className="admin-section-title flex items-center gap-2 mb-0">Onboarding Analytics</h2>

          {!onboardingInsights ? (
            <div className="admin-card bg-white rounded-2xl border border-[#E1E4E8] shadow-sm p-6 text-center text-sm" style={{ color: TEXT_DESC }}>
              No onboarding data yet.
            </div>
          ) : (
            <>
              {/* Top-line stats */}
              <div className="relative bg-white rounded-2xl border-2 border-black/10 overflow-hidden">
                <div className="relative flex items-stretch">
                  <div className="flex-1 flex flex-col items-center justify-center py-3.5 sm:py-4 px-1 gap-1">
                    <p className="font-space text-lg sm:text-xl font-black text-black leading-none">{onboardingInsights.total}</p>
                    <p className="text-[9px] sm:text-[10px] text-black/35 font-semibold uppercase tracking-wider">Onboarded</p>
                  </div>
                  <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />
                  <div className="flex-1 flex flex-col items-center justify-center py-3.5 sm:py-4 px-1 gap-1">
                    <p className="font-space text-lg sm:text-xl font-black text-black leading-none">{onboardingInsights.withExamDatePct}%</p>
                    <p className="text-[9px] sm:text-[10px] text-black/35 font-semibold uppercase tracking-wider">Have exam date</p>
                    <p className="text-[10px] text-black/45">{onboardingInsights.withExamDate} users</p>
                  </div>
                  <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />
                  <div className="flex-1 flex flex-col items-center justify-center py-3.5 sm:py-4 px-1 gap-1">
                    <p className="font-space text-lg sm:text-xl font-black text-black leading-none">{onboardingInsights.takenBeforePct}%</p>
                    <p className="text-[9px] sm:text-[10px] text-black/35 font-semibold uppercase tracking-wider">Taken exam before</p>
                    <p className="text-[10px] text-black/45">{onboardingInsights.takenBefore} users</p>
                  </div>
                </div>
              </div>

              {/* Purpose breakdown */}
              <div className="admin-card bg-white rounded-2xl border border-[#E1E4E8] shadow-sm p-4 sm:p-5">
                <h3 className="text-sm font-semibold mb-3" style={{ color: TEXT_HEADING }}>Purpose</h3>
                <div className="space-y-3">
                  {onboardingInsights.purposes.map((item) => (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-black/80">{item.name}</span>
                        <span className="text-xs font-bold text-black/55">{item.count} ({item.pct}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                        <div className="h-full rounded-full bg-[#006D77]" style={{ width: `${item.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Exam provider + Level, side by side on desktop */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="admin-card bg-white rounded-2xl border border-[#E1E4E8] shadow-sm p-4 sm:p-5">
                  <h3 className="text-sm font-semibold mb-3" style={{ color: TEXT_HEADING }}>Exam Provider</h3>
                  {onboardingInsights.providers.length === 0 ? (
                    <p className="text-sm text-black/40">No data</p>
                  ) : (
                    <div className="space-y-3">
                      {onboardingInsights.providers.map((item) => (
                        <div key={item.name}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-black/80">{item.name}</span>
                            <span className="text-xs font-bold text-black/55">{item.count} ({item.pct}%)</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                            <div className="h-full rounded-full bg-[#3A86FF]" style={{ width: `${item.pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="admin-card bg-white rounded-2xl border border-[#E1E4E8] shadow-sm p-4 sm:p-5">
                  <h3 className="text-sm font-semibold mb-3" style={{ color: TEXT_HEADING }}>Level</h3>
                  {onboardingInsights.levels.length === 0 ? (
                    <p className="text-sm text-black/40">No data</p>
                  ) : (
                    <div className="space-y-3">
                      {onboardingInsights.levels.map((item) => (
                        <div key={item.name}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-black/80">{item.name}</span>
                            <span className="text-xs font-bold text-black/55">{item.count} ({item.pct}%)</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                            <div className="h-full rounded-full bg-[#6366f1]" style={{ width: `${item.pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Plan selection */}
              <div className="admin-card bg-white rounded-2xl border border-[#E1E4E8] shadow-sm p-4 sm:p-5">
                <h3 className="text-sm font-semibold mb-3" style={{ color: TEXT_HEADING }}>Plan Selected</h3>
                <div className="space-y-3">
                  {onboardingInsights.plans.map((item) => (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-black/80">{item.name}</span>
                        <span className="text-xs font-bold text-black/55">{item.count} ({item.pct}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                        <div className="h-full rounded-full bg-[#E29578]" style={{ width: `${item.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      </main>
    </div>
  );
};

export default AdminDashboard;